(() => {
  if (window.__LaleyPdpCleanV31Loaded) return;
  window.__LaleyPdpCleanV31Loaded = true;

  const SECTION_SELECTOR = '[data-pdp-section]';
  const instances = new WeakMap();

  const cssEscape = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(String(value));
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  };

  const escapeHTML = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const OUTFIT_BADGE_FONT = 'Inter, "Helvetica Neue", Arial, sans-serif';

  const applyOutfitBadgeTypography = (root = document) => {
    const badges = root.matches?.('.pdp-clean__outfit-card-badge')
      ? [root]
      : Array.from(root.querySelectorAll?.('.pdp-clean__outfit-card-badge') || []);
    badges.forEach((badge) => {
      if (
        badge.style.getPropertyValue('font-family') === OUTFIT_BADGE_FONT
        && badge.style.getPropertyPriority('font-family') === 'important'
      ) return;
      badge.style.setProperty('font-family', OUTFIT_BADGE_FONT, 'important');
    });
  };

  const normalizeSearchText = (value) => String(value || '')
    .toLowerCase()
    .replace(/å/g, 'a')
    .replace(/ø/g, 'o')
    .replace(/æ/g, 'ae')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const readKlaviyoMetafieldReviewState = () => {
    const scripts = document.querySelectorAll('script#viewed_product, script');
    for (const script of scripts) {
      const text = script.textContent || '';
      if (!text.includes('MetafieldReviews')) continue;
      const match = text.match(/MetafieldReviews\s*=\s*(\{[^;]+?\});/);
      if (!match) continue;
      try {
        const parsed = JSON.parse(match[1]);
        const count = Number(parsed.rating_count || 0);
        const rating = Number(String(parsed.rating?.value || parsed.rating || 5).replace(',', '.')) || 5;
        if (count > 0) return { count, rating: Math.max(0, Math.min(5, rating)) };
      } catch (e) {
        // Klaviyo owns this script shape; ignore and fall back to widget text.
      }
    }
    return null;
  };

  const reviewStars = (rating) => {
    const rounded = Math.max(1, Math.min(5, Math.round(Number(rating || 5))));
    return `${'★'.repeat(rounded)}${'☆'.repeat(5 - rounded)}`;
  };

  const findReviewsRoot = (section) => section?.querySelector?.('#klaviyo-reviews-all') || document.querySelector('#klaviyo-reviews-all');
  const findReviewsSection = (section) => section?.querySelector?.('[data-pdp-reviews-section]') || document.querySelector('[data-pdp-reviews-section]');
  const findReviewsCountSlot = (section) => section?.querySelector?.('[data-pdp-reviews-tab-count]') || document.querySelector('[data-pdp-reviews-tab-count]');

  const primeReviewFallback = (section) => {
    const block = section?.querySelector?.('[data-pdp-reviews-summary]');
    if (!block) return;
    const root = findReviewsRoot(section);
    const countSlot = findReviewsCountSlot(section);
    const attrCount = Number(block.dataset.reviewCount || root?.dataset.reviewCount || 0);
    const attrRating = Number(String(block.dataset.reviewRating || root?.dataset.reviewRating || 5).replace(',', '.')) || 5;
    const state = attrCount > 0
      ? { count: attrCount, rating: Math.max(0, Math.min(5, attrRating)) }
      : readKlaviyoMetafieldReviewState();
    if (!state?.count) return;

    block.dataset.reviewCount = String(state.count);
    block.dataset.reviewRating = String(state.rating || 5);
    root?.setAttribute('data-review-count', String(state.count));
    root?.setAttribute('data-review-rating', String(state.rating || 5));
    if (countSlot) {
      countSlot.textContent = `(${state.count})`;
      countSlot.hidden = false;
    }
    block.classList.add('has-reviews');
    block.classList.remove('is-loading', 'is-empty', 'is-confirmed-empty');

    const liveWidget = block.querySelector('.kl_reviews__star_rating_widget, .klaviyo-star-rating-widget, [class*="star_rating"]');
    const liveWidgetText = `${liveWidget?.textContent || ''} ${liveWidget?.getAttribute?.('aria-label') || ''} ${liveWidget?.getAttribute?.('title') || ''}`;
    const hasLiveWidget = liveWidget && /\d+\s*(?:reviews?|ratings?|anmeldelser?|vurderinger?)/i.test(liveWidgetText);
    const existing = block.querySelector('[data-pdp-reviews-fallback]');
    if (hasLiveWidget) {
      existing?.remove();
      return;
    }
    const fallback = existing || document.createElement('button');
    fallback.type = 'button';
    fallback.className = 'pdp-clean__reviews-fallback';
    fallback.setAttribute('data-pdp-reviews-fallback', '');
    fallback.setAttribute('aria-label', `${state.count} anmeldelser`);
    fallback.innerHTML = `<span class="pdp-clean__reviews-fallback-stars" aria-hidden="true">${reviewStars(state.rating)}</span><span>${escapeHTML(state.count)} anmeldelser</span>`;
    if (!existing) block.prepend(fallback);
  };

  const SIBLING_COLOR_TOKENS = [
    { token: 'grey-melange', label: 'Gråmelert', hex: '#9f9a93' },
    { token: 'soft-pink', label: 'Soft pink', hex: '#f1d1d8' },
    { token: 'deep-pink', label: 'Deep pink', hex: '#c57c8d' },
    { token: 'off-white', label: 'Offwhite', hex: '#f6f0e7' },
    { token: 'offwhite', label: 'Hvit', hex: '#f6f0e7' },
    { token: 'espresso', label: 'Espresso', hex: '#33211d' },
    { token: 'brown', label: 'Brun', hex: '#5a3a2b' },
    { token: 'brun', label: 'Brun', hex: '#5a3a2b' },
    { token: 'black', label: 'Svart', hex: '#111111' },
    { token: 'svart', label: 'Svart', hex: '#111111' },
    { token: 'white', label: 'Hvit', hex: '#ffffff' },
    { token: 'hvit', label: 'Hvit', hex: '#ffffff' },
    { token: 'cream', label: 'Krem', hex: '#efe3d5' },
    { token: 'beige', label: 'Beige', hex: '#d8c4aa' },
    { token: 'pink', label: 'Rosa', hex: '#f2cbd2' },
    { token: 'rosa', label: 'Rosa', hex: '#f2cbd2' },
    { token: 'navy', label: 'Navy', hex: '#182236' },
    { token: 'blue', label: 'Blå', hex: '#7d9abc' },
    { token: 'bla', label: 'Blå', hex: '#7d9abc' },
    { token: 'green', label: 'Grønn', hex: '#6f7f64' },
    { token: 'gronn', label: 'Grønn', hex: '#6f7f64' },
    { token: 'red', label: 'Rød', hex: '#9d4141' },
    { token: 'rod', label: 'Rød', hex: '#9d4141' },
    { token: 'grey', label: 'Grå', hex: '#929292' },
    { token: 'gray', label: 'Grå', hex: '#929292' },
    { token: 'gra', label: 'Grå', hex: '#929292' },
  ].sort((a, b) => b.token.length - a.token.length);

  const readJSON = (el, fallback) => {
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent || '');
    } catch (error) {
      console.warn('[PDP] Invalid JSON', error);
      return fallback;
    }
  };

  const PDP_BOOT_SEQUENCE = Object.freeze([
    { name: 'variants', methods: ['bindOptions'] },
    { name: 'commerce', methods: ['bindForm', 'bindOutfitAdds', 'bindTabs', 'bindCartPackshots'] },
    { name: 'outfitBadges', methods: ['bindOutfitBadgeTypography'] },
    { name: 'gallery', methods: ['bindGallery'] },
    { name: 'productInfo', methods: ['bindReviewsJump', 'bindSizeGuide', 'bindModelInfoOverlay'] },
    { name: 'reviews', methods: ['bindReviewLabelLocale', 'bindReviewWidgetPolish', 'bindKundeneSier'] },
    { name: 'sticky', methods: ['bindSticky'] },
    { name: 'inventory', methods: ['bindBisObserver'] },
    { name: 'bundle', methods: ['bindBundleAutoDiscover', 'bindBundleParent', 'bindBundleSizeSync', 'bindBundleMaterials'] },
    { name: 'galleryEnhancements', methods: ['bindGalleryZoom'] },
    { name: 'siblings', methods: ['bindSiblingColorPicker'] },
  ]);

  class LaleyPDP {
    constructor(section) {
      this.section = section;
      this.variants = readJSON(section.querySelector('[data-pdp-variants]'), []);
      this.settings = readJSON(section.querySelector('[data-pdp-settings]'), {});
      this.unsubs = [];
      this.timers = [];
      this.raf = null;
      this.stickyRaf = null;
      this.mobileStickyHeight = 58;
      this.cartPackshotCache = new Map();
      this.requiresSizeSelection = section.dataset.requiresSizeSelection === 'true';
      this.currentVariant = this.variantFromUrl() || this.variantFromInput() || (this.requiresSizeSelection ? null : (this.variants[0] || null));
    }

    init() {
      this.cache();
      this.bootModules(PDP_BOOT_SEQUENCE);
      const urlParams = new URLSearchParams(window.location.search);
      const hasVariantParam = urlParams.has('variant');
      if (hasVariantParam) this.userPicked = true;

      // Hvis URL-variant er utsolgt men andre varianter HAR stock,
      // switch til første tilgjengelige slik at ATC blir clickable. Uten URL-param
      // skal produkter med størrelser starte uten valgt størrelse.
      const startVariant = (this.currentVariant && (!this.requiresSizeSelection || hasVariantParam))
        ? (this.currentVariant.available ? this.currentVariant : (this.variants.find(v => v.available) || this.currentVariant))
        : this.currentVariant;

      if (startVariant && (startVariant !== this.currentVariant || hasVariantParam)) {
        this.syncOptionInputsToVariant(startVariant);
        this.currentVariant = startVariant;
      }

      // Hvis URL har variant-param betyr det at brukeren ankom via delelink eller
      // har vært på siden før → behandle som "userPicked" så dropdown-trigger viser
      // valgt størrelse og ATC sier "Legg i handlekurv".
      if (this.requiresSizeSelection && !hasVariantParam) {
        this.currentVariant = null;
        if (this.variantInput) this.variantInput.value = '';
      }

      // CP/LSF-stil: ingen size pre-valgt. Force-uncheck alle ikke-color radioer
      // ved init (unntatt hvis bruker ankom via delelink med variant-param).
      if (!this.userPicked) {
        this.optionGroups.forEach((group) => {
          const legend = (group.querySelector('legend')?.textContent || '').toLowerCase();
          const isColor = /farge|color|colour/.test(legend);
          if (isColor) return;
          group.querySelectorAll('[data-pdp-option-input]').forEach((input) => {
            input.checked = false;
          });
        });
      }

      this.renderVariant(this.currentVariant, {
        updateUrl: false,
        syncGallery: Boolean(this.currentVariant),
        behavior: 'auto',
      });
      this.updateOptionStates();
      // Hvis userPicked (delelink), fyll dropdown-triggere med valgte verdier
      if (this.userPicked) {
        this.section.querySelectorAll('[data-pdp-dropdown]').forEach((dropdown) => {
          const input = dropdown.querySelector('[data-pdp-option-input]:checked');
          const trigger = dropdown.querySelector('[data-pdp-dropdown-value]');
          if (input && trigger) trigger.textContent = input.value;
        });
      }
      // Klaviyo Reviews-widget hydrerer noen ganger først ved variant-change.
      // Dispatch en sekundær variant:change-event etter init slik at widgeten
      // får signal om at produkt-konteksten er klar — gir konsistent visning
      // av stjerner + count uten at brukeren må klikke en størrelse først.
      this.nudgeKlaviyoHydration();
    }

    bootModules(sequence) {
      sequence.forEach((module) => {
        module.methods.forEach((method) => {
          if (typeof this[method] !== 'function') {
            console.warn(`[PDP] Missing ${module.name} module method: ${method}`);
            return;
          }
          this[method]();
        });
      });
    }

    nudgeKlaviyoHydration() {
      const fire = () => {
        const variantForEvent = this.currentVariant || this.variantFromInput() || this.variants[0] || null;
        document.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: {
            variant: variantForEvent,
            productId: this.section.dataset.productId,
            sectionId: this.section.dataset.sectionId,
          },
        }));
        // Theme-spesifikk event for kompatibilitet med Laley-tema
        document.dispatchEvent(new CustomEvent('theme:variant:change', {
          bubbles: true,
          detail: { variant: variantForEvent, productId: this.section.dataset.productId },
        }));
        this.pokeKlaviyoReviews();
      };
      // Flere lette pulser: Klaviyo Reviews kan hydrate etter app-script, etter
      // product JSON eller etter variant events. Pulser er product-scoped og billige.
      fire();
      [650, 1600, 3200, 6400].forEach((delay) => {
        const t = window.setTimeout(fire, delay);
        this.timers.push(t);
      });
    }

    pokeKlaviyoReviews() {
      const productId = this.section.dataset.productId;
      if (!productId) return;
      const targets = new Set([
        ...this.section.querySelectorAll('[data-pdp-reviews-summary]'),
        ...document.querySelectorAll('#klaviyo-reviews-all'),
      ]);
      targets.forEach((el) => {
        el.setAttribute('data-id', productId);
        el.setAttribute('data-product-id', productId);
      });
      document.dispatchEvent(new CustomEvent('laley:pdp:reviews-ready', {
        bubbles: true,
        detail: { productId, sectionId: this.section.dataset.sectionId },
      }));
    }

    destroy() {
      this.unsubs.forEach((off) => off());
      this.timers.forEach((timer) => { window.clearInterval(timer); window.clearTimeout(timer); });
      if (this.raf) window.cancelAnimationFrame(this.raf);
      if (this.stickyRaf) window.cancelAnimationFrame(this.stickyRaf);
      this.section.classList.remove('pdp-clean--sticky-visible', 'pdp-clean--gallery-visible');
      instances.delete(this.section);
    }

    cache() {
      this.form = this.section.querySelector('[data-pdp-form]');
      this.variantInput = this.section.querySelector('[data-pdp-variant-id]');
      this.optionInputs = Array.from(this.section.querySelectorAll('[data-pdp-option-input]'));
      this.optionGroups = Array.from(this.section.querySelectorAll('[data-pdp-option-group]'));
      this.atc = this.section.querySelector('[data-pdp-atc]');
      this.error = this.section.querySelector('[data-pdp-error]');
      this.priceCurrent = this.section.querySelector('[data-pdp-price-current]');
      this.priceCompare = this.section.querySelector('[data-pdp-price-compare]');
      this.priceSaving = this.section.querySelector('[data-pdp-price-saving]');
      this.bis = this.section.querySelector('[data-pdp-bis]');
      this.bundle = this.section.querySelector('[data-pdp-bundle]');
      this.bundlePiecePrice = this.section.querySelector('[data-pdp-bundle-piece-price]');
      this.galleryWrap = this.section.querySelector('[data-pdp-gallery-wrap]');
      this.gallery = this.section.querySelector('[data-pdp-gallery]');
      this.galleryProgressBar = this.section.querySelector('[data-pdp-gallery-progress-bar]');
      this.sticky = this.section.querySelector('[data-pdp-sticky-bar]');
      this.stickyButton = this.section.querySelector('[data-pdp-sticky-atc]');
      this.stickyPrice = this.section.querySelector('[data-pdp-sticky-price]');
      this.stickyImg = this.section.querySelector('[data-pdp-sticky-img]');
      this.refreshStickyMetrics();
    }

    refreshStickyMetrics() {
      const cssHeight = parseFloat(
        window.getComputedStyle(this.section).getPropertyValue('--pdp-sticky-mobile-height')
      );
      const measuredHeight = this.sticky && !this.sticky.hidden
        ? Math.ceil(this.sticky.getBoundingClientRect().height)
        : 0;
      const fallbackHeight = Number.isFinite(cssHeight) && cssHeight > 0 ? cssHeight : 58;
      const rawHeight = Number.isFinite(measuredHeight) && measuredHeight > 0
        ? measuredHeight
        : fallbackHeight;
      const isPhone = window.matchMedia('(max-width: 749px)').matches;
      const isTablet = window.matchMedia('(max-width: 989px)').matches;
      const minHeight = isPhone ? 58 : 58;
      const maxHeight = isPhone ? 74 : (isTablet ? 82 : 90);
      this.mobileStickyHeight = Math.max(minHeight, Math.min(maxHeight, rawHeight));
      this.section.style.setProperty('--pdp-sticky-mobile-height', `${this.mobileStickyHeight}px`);
    }

    on(target, event, handler, options) {
      if (!target) return;
      target.addEventListener(event, handler, options);
      this.unsubs.push(() => target.removeEventListener(event, handler, options));
    }

    money(cents) {
      if (typeof cents !== 'number') return '';
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && this.settings.moneyFormat) {
        return window.Shopify.formatMoney(cents, this.settings.moneyFormat);
      }
      return `${(cents / 100).toLocaleString('nb-NO', { maximumFractionDigits: 0 })} kr`;
    }

    cleanVariantLabel(variant) {
      const title = String(variant?.title || '').trim();
      if (!title || /^default title$/i.test(title)) return '';
      return title;
    }

    optionGroupDisplayName(group) {
      const triggerName = group?.querySelector?.('.pdp-clean__dropdown-trigger-name')?.textContent?.trim();
      if (triggerName) return triggerName;
      const legendText = group?.querySelector?.('legend')?.textContent || '';
      const name = legendText.split(':')[0].replace(/\s+/g, ' ').trim();
      return name || 'delen';
    }

    formatNorwegianList(items) {
      const list = items.map((item) => String(item || '').trim()).filter(Boolean);
      if (list.length <= 1) return list[0] || '';
      if (list.length === 2) return `${list[0]} og ${list[1]}`;
      return `${list.slice(0, -1).join(', ')} og ${list[list.length - 1]}`;
    }

    bundleSharedSizeInfo(value, sizeGroups = this.bundleSharedSizeGroups || []) {
      const groupList = Array.from(sizeGroups || []);
      const sizeIndexes = groupList
        .map((group) => Number(group.dataset.optionIndex))
        .filter((index) => Number.isFinite(index));
      const sizeIndexSet = new Set(sizeIndexes);
      const selected = this.selectedOptions();
      const matchesNonSizeSelection = (variant) => selected.every((selectedValue, index) => {
        if (!selectedValue || sizeIndexSet.has(index)) return true;
        return variant.options[index] === selectedValue;
      });

      const exactMatches = this.variants.filter((variant) => (
        matchesNonSizeSelection(variant)
        && sizeIndexes.every((index) => variant.options[index] === value)
      ));

      const parts = groupList.map((group) => {
        const index = Number(group.dataset.optionIndex);
        const matchesPart = this.variants.filter((variant) => (
          matchesNonSizeSelection(variant)
          && variant.options[index] === value
        ));
        return {
          index,
          name: this.optionGroupDisplayName(group),
          exists: matchesPart.length > 0,
          available: matchesPart.some((variant) => variant.available),
        };
      });

      const availableParts = parts.filter((part) => part.available);
      const unavailableParts = parts.filter((part) => part.exists && !part.available);

      return {
        value,
        exactExists: exactMatches.length > 0,
        exactAvailable: exactMatches.some((variant) => variant.available),
        availableParts,
        unavailableParts,
        canMix: availableParts.length > 0 && !exactMatches.some((variant) => variant.available),
      };
    }

    bundleSharedStatusCopy(value, info = this.bundleSharedSizeInfo(value)) {
      const label = String(value || '').trim();
      if (!label) return { title: '', copy: '' };
      if (info.exactAvailable) return { title: '', copy: '' };
      const availableParts = this.formatNorwegianList(info.availableParts.map((part) => `${part.name} ${label}`));
      const unavailableParts = this.formatNorwegianList(info.unavailableParts.map((part) => part.name));
      if (!info.exactExists) {
        return {
          title: `${label} finnes ikke samlet`,
          copy: 'Miks størrelser og velg størrelse per plagg.',
        };
      }
      if (info.availableParts.length > 0 && info.unavailableParts.length > 0) {
        return {
          title: `${unavailableParts} utsolgt i ${label}`,
          copy: `${availableParts} finnes. Miks størrelser for å kjøpe settet.`,
        };
      }
      if (info.availableParts.length > 1 && info.unavailableParts.length === 0) {
        return {
          title: `${label} kan ikke kjøpes samlet`,
          copy: 'Miks størrelser og velg en tilgjengelig kombinasjon.',
        };
      }
      return {
        title: `${label} er utsolgt`,
        copy: 'Få varsel når størrelsen er tilbake.',
      };
    }

    bundleMixedStatusCopy(variant) {
      if (!variant || variant.available) return null;
      const sharedVisible = this.section.querySelector('[data-pdp-bundle-shared]:not([hidden])');
      if (sharedVisible) return null;
      const groups = Array.from(this.bundleSharedSizeGroups || []);
      if (groups.length < 2) return null;

      const bundleSizeIndexes = this.bundleSizeOptionIndexes();
      const selected = this.selectedOptions();
      const matchesNonBundleSelection = (candidate) => selected.every((selectedValue, index) => {
        if (!selectedValue || bundleSizeIndexes.has(index)) return true;
        return candidate.options[index] === selectedValue;
      });

      const parts = groups.map((group) => {
        const index = Number(group.dataset.optionIndex);
        const value = group.querySelector('[data-pdp-option-input]:checked')?.value || selected[index] || variant.options?.[index] || '';
        const matches = this.variants.filter((candidate) => (
          matchesNonBundleSelection(candidate)
          && candidate.options[index] === value
        ));
        return {
          name: this.optionGroupDisplayName(group),
          value,
          exists: matches.length > 0,
          available: matches.some((candidate) => candidate.available),
        };
      }).filter((part) => part.value);

      const unavailable = parts.filter((part) => part.exists && !part.available);
      if (!unavailable.length) return null;
      const available = parts.filter((part) => part.available);
      const unavailableLabel = this.formatNorwegianList(unavailable.map((part) => `${part.name} ${part.value}`));
      const unavailableNames = this.formatNorwegianList(unavailable.map((part) => part.name));
      const availableLabel = this.formatNorwegianList(available.map((part) => `${part.name} ${part.value}`));
      return {
        title: `${unavailableLabel} er utsolgt`,
        copy: availableLabel
          ? `${availableLabel} finnes. Velg en annen størrelse på ${unavailableNames}.`
          : 'Velg en annen størrelse eller få varsel når den er tilbake.',
      };
    }

    showBundleSharedNote(pill) {
      const note = this.bundleShared?.querySelector('[data-pdp-bundle-shared-note]');
      if (!note) return;
      const title = pill?.dataset.statusTitle || '';
      const copy = pill?.dataset.statusCopy || '';
      if (!title && !copy) {
        note.hidden = true;
        note.removeAttribute('aria-label');
        note.replaceChildren();
        return;
      }
      note.innerHTML = `${title ? `<strong>${escapeHTML(title)}</strong>` : ''}${copy ? `<span>${escapeHTML(copy)}</span>` : ''}`;
      note.setAttribute('aria-label', [title, copy].filter(Boolean).join('. '));
      note.hidden = false;
    }

    hideBundleSharedNote() {
      const note = this.bundleShared?.querySelector('[data-pdp-bundle-shared-note]');
      if (!note) return;
      note.hidden = true;
      note.removeAttribute('aria-label');
      note.replaceChildren();
    }

    productTitle() {
      return this.section.querySelector('.pdp-clean__title')?.textContent?.trim() || 'produktet';
    }

    cartUrl(path) {
      const root = window.Shopify?.routes?.root || '/';
      return `${root}${String(path).replace(/^\//, '')}`;
    }

    async fetchProductJson(handle) {
      const normalizedHandle = String(handle || '').trim().replace(/^\/+|\/+$/g, '');
      if (!normalizedHandle) return null;
      if (!this.productJsonCache) this.productJsonCache = new Map();
      if (this.productJsonCache.has(normalizedHandle)) return this.productJsonCache.get(normalizedHandle);

      let product = null;
      try {
        const response = await fetch(this.cartUrl(`products/${normalizedHandle}.js`), { headers: { Accept: 'application/json' } });
        if (response.ok) product = await response.json();
      } catch (e) {
        product = null;
      }

      this.productJsonCache.set(normalizedHandle, product);
      return product;
    }

    variantFromInput() {
      const id = this.variantInput?.value || this.section.querySelector('[data-pdp-variant-id]')?.value;
      return this.variants.find((variant) => String(variant.id) === String(id));
    }

    variantFromUrl() {
      const id = new URLSearchParams(window.location.search).get('variant');
      if (!id) return null;
      return this.variants.find((variant) => String(variant.id) === String(id)) || null;
    }

    hasRequiredSizeSelection() {
      if (!this.requiresSizeSelection) return true;
      const groups = this.optionGroups.filter((group) => group.hasAttribute('data-pdp-size-group'));
      if (!groups.length) return true;
      return groups.every((group) => Boolean(group.querySelector('[data-pdp-option-input]:checked')));
    }

    bundleSizeOptionIndexes() {
      const groups = Array.from(this.bundleSharedSizeGroups || []);
      if (groups.length < 2) return new Set();
      return new Set(groups
        .map((group) => Number(group.dataset.optionIndex))
        .filter((index) => Number.isFinite(index)));
    }

    selectedOptions() {
      if (!this.optionGroups.length) return this.currentVariant?.options || [];
      // Index by the input's real Shopify option position, NOT picker DOM order.
      // The template renders colour before size for visual reasons, but Shopify's
      // option order can be the reverse (e.g. variant title "XS / Black" → size is
      // option 1). findVariant/updateSelectedLabels/updateOptionStates all read this
      // back by data-option-index, so it must be index-aligned or the values swap.
      const opts = [];
      this.optionGroups.forEach((group) => {
        const checked = group.querySelector('[data-pdp-option-input]:checked');
        if (!checked) return;
        const idx = Number(checked.dataset.optionIndex);
        opts[idx] = checked.value;
      });
      return opts;
    }

    findVariant(options = this.selectedOptions()) {
      if (!options.length) return this.variants[0] || null;
      return this.variants.find((variant) => {
        return variant.options.length === options.length && variant.options.every((option, index) => option === options[index]);
      }) || null;
    }

    syncOptionInputsToVariant(variant) {
      if (!variant?.options?.length) return;
      variant.options.forEach((value, index) => {
        const input = this.section.querySelector(`[data-pdp-option-input][data-option-index="${index}"][value="${cssEscape(value)}"]`);
        if (input && !input.checked) input.checked = true;
      });
    }

    bindOptions() {
      this.optionInputs.forEach((input) => {
        this.on(input, 'change', () => {
          // CP-stil affordance: ATC sier "Velg din størrelse" inntil bruker
          // har trigget minst én option-change. Etter første klikk: vis pris.
          this.userPicked = true;
          const variant = this.findVariant();
          this.renderVariant(variant, { updateUrl: true, syncGallery: true, behavior: 'smooth' });
          this.updateOptionStates();
          // Lukk dropdown etter valg + oppdater trigger-verdi
          const dropdown = input.closest('[data-pdp-dropdown]');
          if (dropdown) {
            dropdown.open = false;
            const trigger = dropdown.querySelector('[data-pdp-dropdown-value]');
            if (trigger) trigger.textContent = input.value;
          }
        });
      });
    }

    updateOptionStates() {
      if (!this.optionInputs.length) return;
      const selected = this.selectedOptions();
      const bundleSizeIndexes = this.bundleSizeOptionIndexes();

      this.optionInputs.forEach((input) => {
        const optionIndex = Number(input.dataset.optionIndex);
        const candidate = selected.slice();
        candidate[optionIndex] = input.value;
        const isBundleSizeOption = bundleSizeIndexes.size > 1 && bundleSizeIndexes.has(optionIndex);

        const matches = this.variants.filter((variant) => {
          return variant.options.every((value, index) => {
            if (index === optionIndex) return value === input.value;
            // Bundle-deler skal ikke arve "utsolgt" fra en annen valgt del.
            // Eks: genser XL finnes selv om shorts XL er utsolgt.
            if (isBundleSizeOption && bundleSizeIndexes.has(index)) return true;
            return !candidate[index] || value === candidate[index];
          });
        });

        const exists = matches.length > 0;
        const anyAvailable = matches.some((variant) => variant.available);
        const label = this.section.querySelector(`label[for="${cssEscape(input.id)}"]`);

        input.disabled = !exists;
        if (label) label.dataset.soldout = exists && !anyAvailable ? 'true' : 'false';
      });
      this.updateBundleSharedState();
    }

    renderVariant(variant, options = {}) {
      this.currentVariant = variant || null;
      this.clearError();

      if (this.variantInput && variant) {
        this.variantInput.value = variant.id;
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.updateSelectedLabels();
      this.updatePrice(variant);
      this.updateButtons(variant);
      this.updateVariantStatus(variant);
      this.updateBis(variant);
      this.updateBundle(variant);
      this.updateStickyContent(variant);
      this.updateSizePillsStock();

      if (options.syncGallery && variant) this.syncGalleryToVariant(variant, options.behavior || 'smooth');
      if (options.updateUrl && variant) this.updateUrl(variant);

      document.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: {
          variant,
          productId: this.section.dataset.productId,
          sectionId: this.section.dataset.sectionId,
        },
      }));
    }

    updateSelectedLabels() {
      const selected = this.selectedOptions();
      this.section.querySelectorAll('[data-pdp-selected-option]').forEach((label) => {
        const index = Number(label.dataset.optionIndex);
        if (selected[index]) {
          label.textContent = this.localizeColor(selected[index]);
          return;
        }
        const group = this.optionGroups.find((item) => Number(item.dataset.optionIndex) === index);
        const isColor = Boolean(group?.querySelector('.pdp-clean__option--swatch'));
        label.textContent = isColor ? '' : 'Velg';
      });
    }

    // Norsk VISNINGSNAVN for fargeverdier. Dictionary kommer fra theme-setting
    // (data-pdp-color-map = "Black:Svart, ..."), så det er en redigerbar kilde, ikke
    // hardkodet. Variantverdien beholdes uendret (f.eks. "Black"); kun visning byttes.
    // Ukjente verdier (størrelser som XS) returneres uendret.
    localizeColor(value) {
      if (!this._colorMap) {
        this._colorMap = {};
        (this.section.dataset.pdpColorMap || '').split(',').forEach((pair) => {
          const i = pair.indexOf(':');
          if (i === -1) return;
          const k = pair.slice(0, i).trim().toLowerCase().replace(/\s+/g, '');
          const v = pair.slice(i + 1).trim();
          if (k && v) this._colorMap[k] = v;
        });
      }
      const key = String(value).toLowerCase().replace(/\s+/g, '');
      return this._colorMap[key] || value;
    }

    siblingColorForHandle(handle) {
      const normalized = String(handle || '').toLowerCase().replace(/_/g, '-');
      return SIBLING_COLOR_TOKENS.find((color) => normalized.endsWith(`-${color.token}`) || normalized === color.token) || null;
    }

    siblingBaseHandle(handle) {
      const normalized = String(handle || '').toLowerCase().replace(/_/g, '-');
      const color = this.siblingColorForHandle(normalized);
      const base = color ? normalized.slice(0, -(`-${color.token}`).length) : normalized;
      return /(^|-)caps?($|-)/.test(base) ? 'laley-caps' : base;
    }

    siblingTextToken(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/å/g, 'a')
        .replace(/ø/g, 'o')
        .replace(/æ/g, 'ae')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    stripSiblingColorFromTitle(title) {
      const words = String(title || '').trim().split(/\s+/).filter(Boolean);
      if (words.length <= 1) return String(title || '').trim();

      if (!this.siblingTitleColorTokens) {
        this.siblingTitleColorTokens = new Set();
        SIBLING_COLOR_TOKENS.forEach((color) => {
          this.siblingTitleColorTokens.add(this.siblingTextToken(color.token));
          this.siblingTitleColorTokens.add(this.siblingTextToken(color.label));
        });
      }

      for (let size = Math.min(3, words.length - 1); size >= 1; size -= 1) {
        const tail = this.siblingTextToken(words.slice(-size).join(' '));
        if (this.siblingTitleColorTokens.has(tail)) return words.slice(0, -size).join(' ');
      }

      return String(title || '').trim();
    }

    async siblingLastImage(handle) {
      const normalizedHandle = String(handle || '').trim();
      if (!normalizedHandle) return '';
      if (!this.siblingImageCache) this.siblingImageCache = new Map();
      if (this.siblingImageCache.has(normalizedHandle)) return this.siblingImageCache.get(normalizedHandle);

      let image = '';
      const data = await this.fetchProductJson(normalizedHandle);
      const images = Array.isArray(data?.images) ? data.images : [];
      image = images.length ? images[images.length - 1] : (data?.featured_image || '');
      if (image && String(image).startsWith('//')) image = `${window.location.protocol}${image}`;

      this.siblingImageCache.set(normalizedHandle, image);
      return image;
    }

    async hydrateSiblingImages(items) {
      await Promise.all(items.map(async (item) => {
        if (!item?.handle) return;
        const image = await this.siblingLastImage(item.handle);
        if (image) item.image = image;
      }));
      return items;
    }

    handleFromProductUrl(url) {
      try {
        const parsed = new URL(url, window.location.origin);
        const match = parsed.pathname.match(/\/products\/([^/?#]+)/);
        return match ? match[1] : '';
      } catch (e) {
        return '';
      }
    }

    siblingProductFromSearch(product) {
      const handle = product?.handle || this.handleFromProductUrl(product?.url || '');
      const color = this.siblingColorForHandle(handle);
      if (!handle || !color) return null;
      return {
        handle,
        base: this.siblingBaseHandle(handle),
        title: product?.title || '',
        url: `/products/${handle}`,
        image: product?.image || product?.featured_image?.url || '',
        color,
      };
    }

    renderSiblingColorPicker(root, items) {
      const swatches = root.querySelector('[data-pdp-sibling-swatches]');
      if (!swatches || !items.length) return;
      const currentHandle = root.dataset.currentHandle || this.section.dataset.productHandle || '';
      const current = items.find((item) => item.handle === currentHandle) || items[0];
      const label = root.querySelector('[data-pdp-sibling-color-label]');
      if (label && current?.color?.label) label.textContent = current.color.label;

      const ordered = items.slice().sort((a, b) => {
        if (a.handle === currentHandle) return -1;
        if (b.handle === currentHandle) return 1;
        return a.color.label.localeCompare(b.color.label, 'nb');
      });

      swatches.replaceChildren(...ordered.map((item) => {
        const swatch = document.createElement('a');
        swatch.className = 'pdp-clean__sibling-swatch';
        swatch.href = item.url;
        swatch.setAttribute('aria-label', item.color.label);
        swatch.dataset.pdpSiblingSwatch = '';
        swatch.dataset.colorToken = item.color.token;
        if (item.handle === currentHandle) {
          swatch.classList.add('is-active');
          swatch.setAttribute('aria-current', 'page');
          this.on(swatch, 'click', (event) => event.preventDefault());
        }

        const media = document.createElement('span');
        media.className = 'pdp-clean__sibling-swatch-media';
        media.setAttribute('aria-hidden', 'true');
        media.style.setProperty('--pdp-sibling-color', item.color.hex);
        if (item.image) media.style.setProperty('--pdp-sibling-image', `url("${item.image}")`);

        const name = document.createElement('span');
        name.className = 'pdp-clean__visually-hidden';
        name.textContent = item.color.label;

        swatch.append(media, name);
        return swatch;
      }));
    }

    async bindSiblingColorPicker() {
      const root = this.section.querySelector('[data-pdp-sibling-colors]');
      if (!root || root.dataset.pdpSiblingBound === 'true') return;
      root.dataset.pdpSiblingBound = 'true';

      const currentHandle = root.dataset.currentHandle || this.section.dataset.productHandle || '';
      const currentColor = this.siblingColorForHandle(currentHandle) || {
        token: root.dataset.currentColorToken || '',
        label: root.dataset.currentColorLabel || '',
        hex: root.dataset.currentColorHex || '#eeeeee',
      };
      const activeSwatch = root.querySelector('[data-pdp-sibling-swatch].is-active');
      if (activeSwatch) this.on(activeSwatch, 'click', (event) => event.preventDefault());
      if (root.dataset.pdpSiblingSource === 'collection') return;
      if (!currentHandle || !currentColor.token) return;

      const current = {
        handle: currentHandle,
        base: this.siblingBaseHandle(currentHandle),
        title: root.dataset.currentTitle || '',
        url: root.dataset.currentUrl || this.section.dataset.productUrl || window.location.pathname,
        image: root.dataset.currentColorImage || '',
        color: currentColor,
      };

      try {
        const strippedTitle = this.stripSiblingColorFromTitle(current.title);
        const queries = [strippedTitle, current.base.replace(/-/g, ' '), current.title]
          .map((query) => String(query || '').trim())
          .filter(Boolean);
        const products = [];
        const seenQueries = new Set();

        for (const query of queries) {
          const normalizedQuery = query.toLowerCase();
          if (seenQueries.has(normalizedQuery)) continue;
          seenQueries.add(normalizedQuery);

          const url = `${this.cartUrl('search/suggest.json')}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last`;
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!response.ok) continue;
          const data = await response.json();
          const resultProducts = data?.resources?.results?.products || [];
          products.push(...resultProducts);
          const hasSibling = resultProducts
            .map((product) => this.siblingProductFromSearch(product))
            .some((item) => item && item.base === current.base && item.handle !== current.handle);
          if (hasSibling) break;
        }

        const siblings = products
          .map((product) => this.siblingProductFromSearch(product))
          .filter((item) => item && item.base === current.base);
        const byHandle = new Map();
        [current, ...siblings].forEach((item) => {
          if (!item?.handle || item.base !== current.base) return;
          if (byHandle.has(item.handle)) return;
          byHandle.set(item.handle, { ...item, url: item.url || `/products/${item.handle}` });
        });
        const items = Array.from(byHandle.values());
        if (items.length > 1) {
          await this.hydrateSiblingImages(items);
          this.renderSiblingColorPicker(root, items);
        }
      } catch (e) {
        // Singleton swatch from Liquid is still usable if predictive search is unavailable.
      }
    }

    // "7 reviews" → "7 anmeldelser" — leser det FAKTISKE antallet fra Klaviyo-widgeten
    // og bytter kun ordet (dynamisk, ikke hardkodet tall). Best-effort: virker der
    // teksten er i light-DOM (ikke shadow-DOM).
    bindReviewLabelLocale() {
      const block = this.section.querySelector('[data-pdp-jump-reviews]');
      const countSlot = findReviewsCountSlot(this.section);
      if (!block) return;
      const cacheKey = `laley:pdp:review-summary:${this.section.dataset.productId || 'unknown'}`;
      const readCache = () => {
        try {
          const value = window.localStorage?.getItem(cacheKey);
          if (!value) return null;
          const parsed = JSON.parse(value);
          const fresh = Date.now() - Number(parsed.ts || 0) < 1000 * 60 * 60 * 24 * 14;
          return fresh && Number(parsed.count) > 0 ? parsed : null;
        } catch (e) {
          return null;
        }
      };
      const writeCache = (state) => {
        try {
          window.localStorage?.setItem(cacheKey, JSON.stringify({ ...state, ts: Date.now() }));
        } catch (e) {
          // localStorage can be disabled in private mode; reviews still hydrate normally.
        }
      };
      const readEmbeddedReviewState = () => {
        const scripts = document.querySelectorAll('script#viewed_product, script');
        for (const script of scripts) {
          const text = script.textContent || '';
          if (!text.includes('MetafieldReviews')) continue;
          const match = text.match(/MetafieldReviews\s*=\s*(\{[^;]+?\});/);
          if (!match) continue;
          try {
            const parsed = JSON.parse(match[1]);
            const count = Number(parsed.rating_count || 0);
            const rating = Number(String(parsed.rating?.value || parsed.rating || 5).replace(',', '.')) || 5;
            if (count > 0) return { count, rating: Math.max(0, Math.min(5, rating)) };
          } catch (e) {
            // Klaviyo owns this script shape; ignore and fall back to widget text.
          }
        }
        return null;
      };
      const extractState = () => {
        const root = findReviewsRoot(this.section);
        const attrCount = Number(block.dataset.reviewCount || root?.dataset.reviewCount || 0);
        const attrRating = Number(String(block.dataset.reviewRating || root?.dataset.reviewRating || 5).replace(',', '.')) || 5;
        if (attrCount > 0) {
          return { count: attrCount, rating: Math.max(0, Math.min(5, attrRating)) };
        }

        const embedded = readEmbeddedReviewState();
        if (embedded?.count > 0) return embedded;

        const candidates = [block.textContent || ''];
        block.querySelectorAll('[aria-label], [title], [data-testid], [class]').forEach((el) => {
          candidates.push(el.getAttribute('aria-label') || '');
          candidates.push(el.getAttribute('title') || '');
          candidates.push(el.getAttribute('data-testid') || '');
          candidates.push(el.className || '');
        });
        const joined = candidates.join(' ');
        const countHit = candidates
          .map((text) => String(text).match(/(\d+)\s*(?:reviews?|anmeldelser?|anmeldelse)\b/i))
          .find(Boolean);
        const ratingHit = joined.match(/([0-5](?:[.,]\d+)?)\s*(?:out of|av)\s*5/i)
          || joined.match(/rating[:\s]+([0-5](?:[.,]\d+)?)/i);
        const count = Number(countHit?.[1] || 0);
        const rating = Number(String(ratingHit?.[1] || 5).replace(',', '.')) || 5;
        return { count, rating: Math.max(0, Math.min(5, rating)) };
      };
      const renderFallback = (state) => {
        if (!state || Number(state.count) < 1) return;
        const liveWidget = block.querySelector('.kl_reviews__star_rating_widget, .klaviyo-star-rating-widget, [class*="star_rating"]');
        const liveWidgetText = `${liveWidget?.textContent || ''} ${liveWidget?.getAttribute?.('aria-label') || ''} ${liveWidget?.getAttribute?.('title') || ''}`;
        const hasLiveWidget = liveWidget && /\d+\s*(?:reviews?|ratings?|anmeldelser?|vurderinger?)/i.test(liveWidgetText);
        const existing = block.querySelector('[data-pdp-reviews-fallback]');
        if (hasLiveWidget) {
          existing?.remove();
          return;
        }
        const rating = Math.round(Number(state.rating || 5) * 2) / 2;
        const fullStars = Math.max(0, Math.min(5, Math.floor(rating)));
        const halfStar = rating % 1 >= 0.5;
        const stars = `${'★'.repeat(fullStars)}${halfStar ? '½' : ''}${'☆'.repeat(Math.max(0, 5 - fullStars - (halfStar ? 1 : 0)))}`;
        const fallback = existing || document.createElement('button');
        fallback.type = 'button';
        fallback.className = 'pdp-clean__reviews-fallback';
        fallback.setAttribute('data-pdp-reviews-fallback', '');
        fallback.setAttribute('aria-label', `${state.count} anmeldelser`);
        fallback.innerHTML = `<span class="pdp-clean__reviews-fallback-stars" aria-hidden="true">${stars}</span><span>${state.count} anmeldelser</span>`;
        if (!existing) block.prepend(fallback);
      };
      const setCount = (value) => {
        if (!countSlot) return;
        const count = Number(String(value || '').match(/\d+/)?.[0] || 0);
        if (!Number.isFinite(count) || count < 1) return;
        countSlot.textContent = `(${count})`;
        countSlot.hidden = false;
      };
      const syncCount = () => {
        const state = extractState();
        const cached = state.count > 0 ? state : readCache();
        if (cached?.count > 0) {
          setCount(cached.count);
          renderFallback(cached);
          block.classList.add('has-reviews');
          block.classList.remove('is-loading', 'is-empty', 'is-confirmed-empty');
          if (state.count > 0) writeCache(state);
          return true;
        }
        return false;
      };
      const localize = () => {
        const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT, {
          acceptNode(node) {
            const parent = node.parentElement;
            if (!parent) return NodeFilter.FILTER_REJECT;
            if (/^(SCRIPT|STYLE|SVG|PATH|STOP)$/i.test(parent.tagName)) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
          },
        });
        let node;
        while ((node = walker.nextNode())) {
          const t = node.nodeValue;
          if (t && /\breviews?\b/i.test(t)) {
            const nt = t.replace(/\breviews\b/gi, 'anmeldelser').replace(/\breview\b/gi, 'anmeldelse');
            if (nt !== t) node.nodeValue = nt;
          }
        }
        syncCount();
      };
      let raf = null;
      const schedule = () => {
        if (raf) return;
        raf = window.requestAnimationFrame(() => {
          raf = null;
          localize();
        });
      };
      localize();
      const obs = new MutationObserver(schedule);
      obs.observe(block, { childList: true, subtree: true, characterData: true });
      [900, 2200, 4600].forEach((delay) => {
        const timer = window.setTimeout(localize, delay);
        this.timers.push(timer);
      });
      const emptyTimer = window.setTimeout(() => {
        if (syncCount()) return;
        block.classList.add('is-empty', 'is-confirmed-empty');
        block.classList.remove('is-loading', 'has-reviews');
      }, 3000);
      this.timers.push(emptyTimer);
      this.unsubs.push(() => {
        if (raf) window.cancelAnimationFrame(raf);
        obs.disconnect();
      });
    }

    bindReviewWidgetPolish() {
      const root = findReviewsRoot(this.section);
      if (!root) return;

      const countSlot = findReviewsCountSlot(this.section);
      const section = findReviewsSection(this.section);
      let visibilityReady = false;
      let latestReviewCount = 0;
      const summary = this.section.querySelector('[data-pdp-reviews-summary]');
      const setCount = (value) => {
        const count = Number(String(value || '').match(/\d+/)?.[0] || 0);
        if (!Number.isFinite(count) || count < 1) return;
        latestReviewCount = count;
        if (countSlot) {
          countSlot.textContent = `(${count})`;
          countSlot.hidden = false;
        }
      };
      setCount(root.dataset.reviewCount || summary?.dataset.reviewCount || '');

      const translate = (value) => {
        let text = String(value || '');
        const trimmed = text.trim();
        const exact = {
          'Customer reviews': 'Kundeanmeldelser',
          'WRITE A REVIEW': 'Skriv anmeldelse',
          'Write a review': 'Skriv anmeldelse',
          Reviews: 'Anmeldelser',
          Review: 'Anmeldelse',
          'SHOW MORE': 'Vis flere',
          'Show more': 'Vis flere',
          'Verified buyer': 'Verifisert kjøper',
          'Most recent': 'Nyeste først',
          'Highest rating': 'Høyest vurdering',
          'Lowest rating': 'Lavest vurdering',
          'Most relevant': 'Mest relevant',
          'All ratings': 'Alle vurderinger',
          'With media': 'Med bilder',
          'Search reviews': 'Søk i anmeldelser',
        };
        if (exact[trimmed]) return text.replace(trimmed, exact[trimmed]);

        text = text
          .replace(/\bCustomer reviews\b/g, 'Kundeanmeldelser')
          .replace(/\bWRITE A REVIEW\b/g, 'Skriv anmeldelse')
          .replace(/\bWrite a review\b/g, 'Skriv anmeldelse')
          .replace(/\bSHOW MORE\b/g, 'Vis flere')
          .replace(/\bShow more\b/g, 'Vis flere')
          .replace(/\bVerified buyer\b/g, 'Verifisert kjøper')
          .replace(/\b(\d+)\s+reviews\b/gi, (_, count) => `${count} anmeldelser`)
          .replace(/\b1\s+review\b/gi, '1 anmeldelse')
          .replace(/\b(\d+)\s+ratings\b/gi, (_, count) => `${count} vurderinger`)
          .replace(/\b1\s+rating\b/gi, '1 vurdering')
          .replace(/\b(\d+)\s+stars\b/gi, (_, count) => `${count} stjerner`)
          .replace(/\b1\s+star\b/gi, '1 stjerne')
          .replace(/\b1\s+day ago\b/gi, 'for 1 dag siden')
          .replace(/\b(\d+)\s+days ago\b/gi, (_, count) => `for ${count} dager siden`)
          .replace(/\b1\s+month ago\b/gi, 'for 1 måned siden')
          .replace(/\b(\d+)\s+months ago\b/gi, (_, count) => `for ${count} måneder siden`)
          .replace(/\b1\s+year ago\b/gi, 'for 1 år siden')
          .replace(/\b(\d+)\s+years ago\b/gi, (_, count) => `for ${count} år siden`);
        return text;
      };

      const syncCount = () => {
        // Use Klaviyo's explicit count nodes only. Broad root text also contains
        // histogram/filter numbers (5 stars, 4 stars, etc.) and can corrupt the
        // PDP tab count.
        const candidates = [
          root.querySelector('.kl_reviews__list__tab small')?.textContent || '',
          root.querySelector('.kl_reviews__summary__stars__count')?.textContent || '',
        ];
        const hit = candidates
          .map((text) => String(text).match(/\b(\d+)\b/))
          .find(Boolean);
        if (hit) setCount(hit[1]);
      };

      const syncVisibility = () => {
        if (!section) return;
        const reviewItems = root.querySelectorAll('.kl_reviews__review_item, .kl_reviews__review__item, [class*="review_item"], [class*="review__item"]');
        const hasRenderedWidget = Boolean(root.querySelector('#klaviyo-product-reviews-wrapper, .reviews_all_container, .kl_reviews__list, .kl_reviews__summary'))
          && root.textContent.trim().length > 0;
        const explicitCount = [
          root.dataset.reviewCount || '',
          summary?.dataset.reviewCount || '',
          root.querySelector('.kl_reviews__list__tab small')?.textContent || '',
          root.querySelector('.kl_reviews__summary__stars__count')?.textContent || '',
        ]
          .map((text) => Number(String(text).match(/\b(\d+)\b/)?.[1] || 0))
          .find((count) => Number.isFinite(count) && count > 0) || 0;
        const hasKnownReviews = latestReviewCount > 0 || explicitCount > 0;
        if (reviewItems.length > 0 || hasRenderedWidget) {
          section.classList.add('has-reviews');
          section.classList.remove('is-loading', 'is-empty', 'is-confirmed-empty');
          return;
        }
        if (hasKnownReviews) {
          section.classList.add('has-reviews', 'is-loading');
          section.classList.remove('is-empty', 'is-confirmed-empty');
          return;
        }
        if (visibilityReady) {
          section.classList.add('is-empty', 'is-confirmed-empty');
          section.classList.remove('is-loading', 'has-reviews');
        }
      };

      const polish = () => {
        const walker = document.createTreeWalker(root, 4, {
          acceptNode(node) {
            const parent = node.parentElement;
            if (!parent) return NodeFilter.FILTER_REJECT;
            if (/^(SCRIPT|STYLE|SVG|PATH|STOP)$/i.test(parent.tagName)) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
          },
        });
        let node;
        while ((node = walker.nextNode())) {
          const next = translate(node.nodeValue);
          if (next !== node.nodeValue) node.nodeValue = next;
        }

        root.querySelectorAll('[aria-label], [title], [placeholder]').forEach((el) => {
          ['aria-label', 'title', 'placeholder'].forEach((attr) => {
            const value = el.getAttribute(attr);
            if (!value) return;
            const next = translate(value);
            if (next !== value) el.setAttribute(attr, next);
          });
        });
        syncCount();
        syncVisibility();
      };

      let polishTimer = null;
      const schedule = () => {
        if (polishTimer) return;
        polishTimer = window.setTimeout(() => {
          polishTimer = null;
          polish();
        }, 90);
      };

      polish();
      const visibilityTimer = window.setTimeout(() => {
        visibilityReady = true;
        polish();
      }, 3800);
      [1200, 2600, 5200, 8800].forEach((delay) => {
        const timer = window.setTimeout(polish, delay);
        this.timers.push(timer);
      });
      const obs = new MutationObserver(schedule);
      obs.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['aria-label', 'title', 'placeholder'] });
      let intersectionObserver = null;
      if ('IntersectionObserver' in window && section) {
        intersectionObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.pokeKlaviyoReviews();
          polish();
        }, { rootMargin: '720px 0px' });
        intersectionObserver.observe(section);
      }
      this.unsubs.push(() => {
        if (polishTimer) window.clearTimeout(polishTimer);
        window.clearTimeout(visibilityTimer);
        obs.disconnect();
        intersectionObserver?.disconnect();
      });
    }

    updatePrice(variant) {
      if (!variant) return;
      if (this.priceCurrent) this.priceCurrent.textContent = this.money(variant.price);
      if (this.priceCompare) {
        this.priceCompare.textContent = variant.compareAtPrice ? this.money(variant.compareAtPrice) : '';
        this.priceCompare.hidden = !variant.isOnSale;
      }
      if (this.priceSaving) {
        const hasSaving = variant.isOnSale && Number(variant.savingPercent) > 0;
        this.priceSaving.textContent = hasSaving ? `-${variant.savingPercent}% — tilbudspris` : '';
        this.priceSaving.hidden = !hasSaving;
      }
      const priceWrap = this.section.querySelector('[data-pdp-price-wrap]');
      if (priceWrap) priceWrap.classList.toggle('is-on-sale', !!variant.isOnSale);
    }

    updateButtons(variant) {
      if (!this.atc) return;
      // Ingen falsk add-to-cart før kunden har valgt størrelse. Pick-mode
      // scroll/fokuserer size-pickeren og viser inline-feil ved klikk.
      const needsSize = !this.hasRequiredSizeSelection();

      if (needsSize) {
        this.atc.disabled = false;
        this.atc.dataset.mode = 'pick';
        this.atc.textContent = 'Velg størrelse';
        return;
      }
      if (!variant) {
        this.atc.disabled = true;
        this.atc.dataset.mode = 'unavailable';
        this.atc.textContent = 'Velg alternativer';
        return;
      }
      if (!variant.available) {
        this.atc.disabled = false;
        this.atc.dataset.mode = 'bis';
        this.atc.textContent = 'Varsle meg';
        return;
      }
      this.atc.disabled = false;
      this.atc.dataset.mode = 'add';
      this.atc.textContent = 'Legg i handlekurv — ';
      const price = document.createElement('span');
      price.setAttribute('data-pdp-atc-price', '');
      price.textContent = this.money(variant.price);
      this.atc.appendChild(price);
    }

    updateSizePillsStock() {
      // CP-stil: vis differensiert melding per size: "Kun X igjen", "Få igjen",
      // "Snart utsolgt", "Utsolgt". Kjører på alle ikke-color grupper (inkl.
      // bundle-komponenter som "Flared Yoga Tights").
      const stockLabel = (variant) => {
        if (!variant || !variant.available) return { text: 'Utsolgt', cls: 'is-out' };
        const qty = Number(variant.inventoryQuantity);
        const tracked = !!variant.inventoryManagement && variant.inventoryPolicy !== 'continue';
        if (!tracked || qty <= 0) return null;
        if (qty === 1) return { text: 'Kun 1 igjen', cls: 'is-low' };
        if (qty === 2) return { text: 'Kun 2 igjen', cls: 'is-low' };
        if (qty <= 4) return { text: 'Få igjen', cls: 'is-low' };
        if (qty <= 6) return { text: 'Få igjen', cls: 'is-low' };
        return null;
      };

      // Iterer over alle "size-like" grupper (alle ikke-color picker-groups),
      // både dropdown og inline pills. Inline pills trenger like tydelig lagerstatus.
      const sizeGroups = this.optionGroups.filter((g) => !g.querySelector('.pdp-clean__option--swatch'));
      const bundleSizeIndexes = this.bundleSizeOptionIndexes();
      sizeGroups.forEach((sizeGroup) => {
        const sizeOptionIndex = Number(sizeGroup.dataset.optionIndex);
        const isBundleSizeGroup = bundleSizeIndexes.size > 1 && bundleSizeIndexes.has(sizeOptionIndex);
        const otherOptionValues = this.optionGroups
          .filter((g) => g !== sizeGroup)
          .map((g) => ({
            index: Number(g.dataset.optionIndex),
            value: g.querySelector('[data-pdp-option-input]:checked')?.value || null,
          }))
          .filter((other) => !(isBundleSizeGroup && bundleSizeIndexes.has(other.index)));

        sizeGroup.querySelectorAll('[data-pdp-option-input]').forEach((input) => {
          const sizeValue = input.value;
          const matches = this.variants.filter((v) => {
            if (v.options[sizeOptionIndex] !== sizeValue) return false;
            return otherOptionValues.every((other) => {
              if (other.value === null) return true;
              return v.options[other.index] === other.value;
            });
          });
          const variant = matches.find((v) => v.available) || matches[0] || null;
          const label = input.nextElementSibling;
          const stockEl = label?.querySelector('[data-pdp-option-stock]');
          if (!stockEl) return;
          stockEl.classList.remove('is-low', 'is-soon', 'is-out');
          const info = stockLabel(variant);
          if (info) {
            stockEl.textContent = info.text;
            stockEl.classList.add(info.cls);
            stockEl.hidden = false;
          } else {
            stockEl.hidden = true;
            stockEl.textContent = '';
          }
        });
      });
    }

    updateVariantStatus(variant) {
      // Kontekstuell beskjed under størrelser. Lavt lager vises på selve
      // størrelsesknappen; denne linjen brukes kun når kunden velger utsolgt.
      const slot = this.section.querySelector('[data-pdp-variant-status]');
      const text = slot?.querySelector('[data-pdp-variant-status-text]');
      if (!slot || !text || !variant) return;
      slot.classList.remove('is-soldout', 'is-low');
      if (!variant.available) {
        const label = this.cleanVariantLabel(variant);
        const sharedPill = this.section.querySelector('[data-pdp-bundle-shared]:not([hidden]) .pdp-clean__bundle-shared-pill.is-selected[data-soldout="true"]');
        if (sharedPill) {
          slot.removeAttribute('aria-label');
          slot.hidden = true;
          text.replaceChildren();
          return;
        }
        const bundleStatus = this.bundleMixedStatusCopy(variant);
        const title = document.createElement('strong');
        const copy = document.createElement('span');
        title.textContent = bundleStatus?.title || (label ? `${label} er utsolgt` : 'Utsolgt');
        copy.textContent = bundleStatus?.copy || 'Få varsel når den er tilbake';
        text.replaceChildren(title, copy);
        slot.setAttribute('aria-label', [title.textContent, copy.textContent].filter(Boolean).join('. '));
        slot.classList.add('is-soldout');
        slot.hidden = false;
      } else {
        slot.removeAttribute('aria-label');
        slot.hidden = true;
      }
    }

    updateBis(variant) {
      // State-maskin for sold-out/Varsle-meg-flow.
      // Klaviyo injekter <a class="klaviyo-bis-trigger"> direkte i form når
      // den vurderer current variant som sold-out. Vi:
      //   1. Setter data-variant-available på root for CSS-targeting
      //   2. Flytter Klaviyo-trigger inn i vår [data-pdp-bis]-slot
      //   3. Skjuler trigger når variant er available
      const isAvailable = Boolean(variant && variant.available);
      this.section.dataset.variantAvailable = isAvailable ? 'true' : 'false';

      const injectedTrigger = this.form?.querySelector('.klaviyo-bis-trigger')
        || this.section.querySelector('.klaviyo-bis-trigger');

      if (this.bis && injectedTrigger && !this.bis.contains(injectedTrigger)) {
        this.bis.appendChild(injectedTrigger);
      }

      const hasTriggerInSlot = Boolean(this.bis?.querySelector('.klaviyo-bis-trigger'));

      if (isAvailable) {
        if (this.bis) this.bis.hidden = true;
        if (injectedTrigger) injectedTrigger.hidden = true;
        return;
      }
      // Sold-out: vis BIS-slot hvis Klaviyo har plassert trigger
      if (this.bis) this.bis.hidden = !hasTriggerInSlot;
      if (injectedTrigger) injectedTrigger.hidden = false;
    }

    createOutfitCard(product, options = {}) {
      if (!product || !product.url) return null;
      const tagText = Array.isArray(product.tags) ? product.tags.join(' ') : String(product.tags || '');
      if (/kommersnart|kommer\s+snart|coming[\s-]?soon/i.test(`${tagText} ${product.handle || ''}`)) return null;
      const variant = product.variants?.[0];
      const currentPrice = Number(product.price || variant?.price || 0);
      const comparePrice = Number(product.compare_at_price_max || product.compare_at_price || variant?.compare_at_price || 0);
      const isSale = comparePrice > currentPrice;
      const savingPercent = isSale ? Math.round(((comparePrice - currentPrice) * 100) / comparePrice) : 0;
      const article = document.createElement('article');
      article.className = `pdp-clean__outfit-card${options.recommendation ? ' pdp-clean__outfit-card--recommendation' : ''}${isSale ? ' is-on-sale' : ''}`;

      const link = document.createElement('a');
      link.href = product.url;
      link.className = 'pdp-clean__outfit-card-link';
      link.setAttribute('aria-label', `Se ${product.title || 'produkt'}`);

      const imageSource = product.featured_image || product.image;
      const imageUrl = typeof imageSource === 'string' ? imageSource : (imageSource?.url || imageSource?.src || '');

      if (imageUrl) {
        const img = document.createElement('img');
        img.className = 'pdp-clean__outfit-card-img';
        img.src = imageUrl;
        img.alt = product.title || '';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.width = 650;
        img.height = 867;
        img.setAttribute('data-pin-nopin', 'true');
        link.appendChild(img);
      }

      const media = document.createElement('div');
      media.className = 'pdp-clean__outfit-card-media';
      media.appendChild(link);
      if (isSale) {
        const badge = document.createElement('span');
        badge.className = 'pdp-clean__outfit-card-badge';
        badge.textContent = `Salg -${savingPercent}%`;
        applyOutfitBadgeTypography(badge);
        media.appendChild(badge);
      }
      article.appendChild(media);

      const info = document.createElement('a');
      info.href = product.url;
      info.className = 'pdp-clean__outfit-card-info';
      const title = document.createElement('p');
      title.className = 'pdp-clean__outfit-card-title';
      title.textContent = product.title || '';
      const price = document.createElement('p');
      price.className = `pdp-clean__outfit-card-price${isSale ? ' is-on-sale' : ''}`;
      const current = document.createElement('span');
      current.className = 'pdp-clean__outfit-card-price-current';
      current.textContent = this.money(currentPrice);
      price.appendChild(current);
      if (isSale) {
        const compare = document.createElement('span');
        compare.className = 'pdp-clean__outfit-card-price-compare';
        compare.textContent = this.money(comparePrice);
        price.appendChild(compare);
      }
      info.append(title, price);
      article.appendChild(info);

      let action = null;
      if (variant && variant.available && product.variants.length === 1) {
        const form = document.createElement('form');
        form.action = this.cartUrl('cart/add');
        form.method = 'post';
        form.className = 'pdp-clean__outfit-form';
        form.setAttribute('data-pdp-outfit-form', '');
        const idInput = document.createElement('input');
        idInput.type = 'hidden';
        idInput.name = 'id';
        idInput.value = String(variant.id);
        const quantityInput = document.createElement('input');
        quantityInput.type = 'hidden';
        quantityInput.name = 'quantity';
        quantityInput.value = '1';
        const button = document.createElement('button');
        button.type = 'submit';
        button.className = 'pdp-clean__outfit-add';
        button.setAttribute('data-pdp-outfit-add', '');
        button.setAttribute('aria-label', `Legg ${product.title || 'produktet'} i handlekurv`);
        button.textContent = 'Legg til';
        form.append(idInput, quantityInput, button);
        action = form;
      } else {
        const anchor = document.createElement('a');
        anchor.className = 'pdp-clean__outfit-add pdp-clean__outfit-add--link';
        anchor.href = product.url;
        anchor.setAttribute('data-pdp-outfit-add', '');
        if (product.handle) anchor.setAttribute('data-pdp-outfit-handle', product.handle);
        anchor.setAttribute('aria-label', `Velg variant for ${product.title || 'produktet'}`);
        anchor.textContent = 'VELG STØRRELSE';
        action = anchor;
      }

      if (action) article.appendChild(action);
      return article;
    }

    bindOutfitBadgeTypography() {
      const outfit = this.section.querySelector('.pdp-clean__outfit') || this.section;
      let raf = null;
      const schedule = () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = null;
          applyOutfitBadgeTypography(outfit);
        });
      };
      schedule();
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            schedule();
            return;
          }
          if (
            mutation.type === 'attributes'
            && mutation.attributeName === 'style'
            && mutation.target?.classList?.contains('pdp-clean__outfit-card-badge')
          ) {
            schedule();
            return;
          }
        }
      });
      observer.observe(outfit, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style'],
      });
      this.unsubs.push(() => {
        if (raf) cancelAnimationFrame(raf);
        observer.disconnect();
      });
    }

    bindOutfitForm(form, bubbles = false) {
      if (!form || form.dataset.pdpOutfitBound === 'true') return;
      form.dataset.pdpOutfitBound = 'true';
      const button = form.querySelector('[data-pdp-outfit-add]');
      const select = form.querySelector('[data-pdp-outfit-select]');
      const idInput = form.querySelector('[data-pdp-outfit-variant-id]') || form.querySelector('input[name="id"]');
      if (!button) return;

      const sync = () => {
        if (!select) {
          button.disabled = !idInput?.value || button.dataset.forceDisabled === 'true';
          return;
        }
        const selected = select.selectedOptions?.[0];
        const value = select.value || '';
        const available = Boolean(value) && selected?.dataset.available !== 'false' && !selected?.disabled;
        if (idInput) idInput.value = available ? value : '';
        button.disabled = !available;
      };

      if (select) {
        this.on(select, 'change', sync);
        sync();
      }

      this.on(form, 'submit', (event) => {
        event.preventDefault();
        event.stopPropagation();
        sync();
        if (button.disabled) {
          if (select) select.focus();
          return;
        }
        this.addOutfitFormToCart(form, button, sync);
      });
    }

    async addOutfitFormToCart(form, button, sync) {
      const formData = new FormData(form);
      const variantId = String(formData.get('id') || '').trim();
      if (!variantId) {
        button.textContent = 'VELG STØRRELSE';
        sync?.();
        return;
      }

      const cartItems = document.querySelector('cart-items');
      if (cartItems) {
        const cleanup = () => {
          button.removeAttribute('aria-busy');
          sync?.();
          this.hydrateCartPackshots();
        };
        button.setAttribute('aria-busy', 'true');
        document.addEventListener('theme:product:added', cleanup, { once: true });
        document.addEventListener('theme:product:add-error', cleanup, { once: true });
        document.dispatchEvent(new CustomEvent('theme:cart:add', {
          bubbles: true,
          cancelable: true,
          detail: { button },
        }));
        return;
      }

      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = 'LEGGER TIL';

      try {
        const response = await fetch(this.cartUrl('cart/add.js'), {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        if (!response.ok) throw new Error('cart/add failed');
        const item = await response.json();
        const cartResponse = await fetch(this.cartUrl('cart.js'), { headers: { Accept: 'application/json' } });
        const cart = cartResponse.ok ? await cartResponse.json() : null;

        this.refreshCartCount(cart?.item_count);
        this.announceCartUpdate(cart, item);
        this.openCartDrawer();
        this.hydrateCartPackshots(cart);

        button.textContent = 'LAGT TIL';
        const timer = window.setTimeout(() => {
          button.textContent = originalText;
          sync?.();
        }, 950);
        this.timers.push(timer);
      } catch (error) {
        button.textContent = 'PRØV IGJEN';
        const timer = window.setTimeout(() => {
          button.textContent = originalText;
          sync?.();
        }, 1400);
        this.timers.push(timer);
      }
    }

    refreshCartCount(count) {
      if (!Number.isFinite(Number(count))) return;
      const text = String(Number(count));
      document.querySelectorAll('[data-cart-count], [data-cart-count-bubble], .cart-count-bubble, .cart-count, .laley-hv2__cart-count').forEach((el) => {
        el.textContent = text;
        el.hidden = false;
        el.setAttribute('aria-hidden', 'false');
      });
    }

    announceCartUpdate(cart, item) {
      const detail = { cart, item };
      ['cart:refresh', 'cart:updated', 'cart:change', 'theme:cart:change', 'theme:cart:updated'].forEach((name) => {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      });
    }

    async productLastImage(handle) {
      const normalizedHandle = String(handle || '').trim().replace(/^\/+|\/+$/g, '');
      if (!normalizedHandle) return '';
      if (this.cartPackshotCache.has(normalizedHandle)) return this.cartPackshotCache.get(normalizedHandle);

      const data = await this.fetchProductJson(normalizedHandle);
      const images = Array.isArray(data?.images) ? data.images : [];
      let image = images.length ? images[images.length - 1] : (data?.featured_image || '');
      if (image && String(image).startsWith('//')) image = `${window.location.protocol}${image}`;
      this.cartPackshotCache.set(normalizedHandle, image || '');
      return image || '';
    }

    cartDrawerRoots() {
      return Array.from(document.querySelectorAll([
        'cart-drawer',
        '#CartDrawer',
        '[data-cart-drawer]',
        '.cart-drawer',
        'cart-notification',
        '[id*="CartDrawer"]',
        '[class*="cart-drawer"]',
      ].join(',')));
    }

    cartRowForAnchor(anchor) {
      return anchor.closest([
        '[data-cart-item]',
        '[data-line-item]',
        '.cart-item',
        '.cart__item',
        '.drawer__item',
        '.line-item',
        'li',
        'tr',
        'article',
        '.grid',
      ].join(',')) || anchor.parentElement;
    }

    cartImageForRow(row) {
      if (!row) return null;
      return Array.from(row.querySelectorAll('img')).find((img) => {
        const copy = `${img.alt || ''} ${img.className || ''} ${img.src || ''}`.toLowerCase();
        if (/logo|icon|payment|klarna|vipps|flag|sprite/.test(copy)) return false;
        const rect = img.getBoundingClientRect();
        return rect.width >= 24 && rect.height >= 24;
      }) || null;
    }

    applyCartPackshotImage(img, imageUrl) {
      if (!img || !imageUrl) return;
      const rect = img.getBoundingClientRect();
      const size = Math.max(54, Math.min(96, Math.round(Math.max(rect.width, rect.height) || 72)));
      const frame = img.parentElement;
      img.src = imageUrl;
      img.removeAttribute('srcset');
      img.setAttribute('sizes', '72px');
      img.classList.add('pdp-clean__cart-packshot');
      img.style.width = `${size}px`;
      img.style.height = `${size}px`;
      img.style.aspectRatio = '1 / 1';
      img.style.objectFit = 'contain';
      img.style.objectPosition = 'center';
      img.style.background = '#fff';
      if (frame) {
        frame.classList.add('pdp-clean__cart-packshot-frame');
        frame.style.width = `${size}px`;
        frame.style.height = `${size}px`;
        frame.style.aspectRatio = '1 / 1';
        frame.style.display = 'flex';
        frame.style.alignItems = 'center';
        frame.style.justifyContent = 'center';
        frame.style.flex = `0 0 ${size}px`;
        frame.style.background = '#fff';
      }
    }

    bindCartPackshots() {
      const scheduleHydrate = () => {
        const run = async () => {
          let cart = null;
          try {
            const response = await fetch(this.cartUrl('cart.js'), { headers: { Accept: 'application/json' } });
            if (response.ok) cart = await response.json();
          } catch (error) {
            cart = null;
          }
          this.hydrateCartPackshots(cart);
        };

        run();
        [180, 520, 1000].forEach((delay) => {
          const timer = window.setTimeout(run, delay);
          this.timers.push(timer);
        });
      };

      [
        'theme:product:added',
        'theme:cart-drawer:open',
        'theme:cart:change',
        'cart:updated',
        'cart:change',
        'cart:refresh',
      ].forEach((eventName) => {
        document.addEventListener(eventName, scheduleHydrate);
        this.unsubs.push(() => document.removeEventListener(eventName, scheduleHydrate));
      });
    }

    hydrateCartPackshots(cart = null) {
      const handlesFromCart = new Set((cart?.items || [])
        .map((item) => item?.handle || this.handleFromProductUrl(item?.url || ''))
        .filter(Boolean));

      const run = async () => {
        const roots = this.cartDrawerRoots();
        if (!roots.length) return;

        const jobs = [];
        const touchedRows = new WeakSet();

        const cartRows = roots.flatMap((root) => Array.from(root.querySelectorAll([
          '[data-item]',
          '[data-cart-item]',
          '[data-line-item]',
          '.cart-item',
          '.cart__item',
          '.drawer__item',
          '.line-item',
        ].join(','))));

        if (cartRows.length) {
          cartRows.forEach((row, index) => {
            if (touchedRows.has(row)) return;
            const anchor = row.querySelector('a[href*="/products/"]');
            const handle = this.handleFromProductUrl(anchor?.getAttribute('href') || anchor?.href || '')
              || (cart?.items || [])[index]?.handle
              || this.handleFromProductUrl((cart?.items || [])[index]?.url || '');
            const img = this.cartImageForRow(row);
            if (!handle || !img) return;
            touchedRows.add(row);
            jobs.push(this.productLastImage(handle).then((imageUrl) => this.applyCartPackshotImage(img, imageUrl)));
          });
        } else {
          roots.forEach((root) => {
          root.querySelectorAll('a[href*="/products/"]').forEach((anchor) => {
            const handle = this.handleFromProductUrl(anchor.getAttribute('href') || anchor.href);
            if (!handle) return;
            const row = this.cartRowForAnchor(anchor);
            if (row && touchedRows.has(row)) return;
            const img = this.cartImageForRow(row) || this.cartImageForRow(anchor);
            if (!img) return;
            if (row) touchedRows.add(row);
            jobs.push(this.productLastImage(handle).then((imageUrl) => this.applyCartPackshotImage(img, imageUrl)));
          });
          });
        }

        if (!jobs.length && handlesFromCart.size) {
          const imgs = roots.flatMap((root) => Array.from(root.querySelectorAll('img')))
            .filter((img) => !img.classList.contains('pdp-clean__cart-packshot'));
          Array.from(handlesFromCart).forEach((handle, index) => {
            const img = imgs[index];
            if (!img) return;
            jobs.push(this.productLastImage(handle).then((imageUrl) => this.applyCartPackshotImage(img, imageUrl)));
          });
        }

        await Promise.allSettled(jobs);
      };

      run();
      [120, 360, 900, 1600].forEach((delay) => {
        const timer = window.setTimeout(run, delay);
        this.timers.push(timer);
      });
    }

    openCartDrawer() {
      const detail = { source: 'pdp-outfit' };
      ['cart:open', 'theme:cart:open', 'ajaxCart:open', 'drawer:open'].forEach((name) => {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      });

      const drawer = document.querySelector('cart-drawer, [data-cart-drawer], #CartDrawer, .cart-drawer');
      if (drawer) {
        ['open', 'show', 'showDrawer'].some((method) => {
          if (typeof drawer[method] !== 'function') return false;
          drawer[method]();
          return true;
        });
        drawer.removeAttribute('hidden');
        drawer.classList.add('is-open', 'active', 'animate');
      }

      const toggle = document.querySelector('[data-cart-toggle], [data-cart-drawer-toggle], [aria-controls="CartDrawer"], [aria-controls="cart-drawer"]');
      if (toggle instanceof HTMLElement) {
        const timer = window.setTimeout(() => toggle.click(), 80);
        this.timers.push(timer);
      }
    }

    bindOutfitForms(scope) {
      scope.querySelectorAll('[data-pdp-outfit-form]').forEach((form) => {
        this.bindOutfitForm(form, true);
      });
    }

    async bindBundleAutoDiscover() {
      const bundle = this.bundle;
      if (!bundle || bundle.dataset.pdpBundleAuto !== 'true') return;
      const prefix = (bundle.dataset.bundlePrefix || '').trim();
      const optionsJsonEl = bundle.querySelector('[data-pdp-bundle-options]');
      const tagsJsonEl = bundle.querySelector('[data-pdp-bundle-tags]');
      if (!prefix || !optionsJsonEl) return;
      let optionNames = [];
      try {
        const parsed = JSON.parse(optionsJsonEl.textContent || '[]');
        optionNames = parsed.map((o) => typeof o === 'string' ? o : (o?.name || '')).filter(Boolean);
      } catch (e) { return; }
      // Filter out generisk "Title"-option (1-option-bundles uten meningsfull option-navn)
      optionNames = optionNames.filter(n => n.toLowerCase() !== 'title');

      // Fallback: hvis ingen meningsfulle options, bruk tags som komponent-identifikatorer
      if (!optionNames.length && tagsJsonEl) {
        try {
          const tagsParsed = JSON.parse(tagsJsonEl.textContent || '[]');
          const generic = new Set(['bundle','set','sett','pack','gavesett','beauty','selvpleie','hjem','klær','klar','laley','workout','yoga','training','treningsklær','treningssett','salg','sale','bestselger','nyhet','presale','final-sale']);
          optionNames = tagsParsed.filter(t => typeof t === 'string' && !generic.has(t.toLowerCase()) && t.length > 2);
        } catch (e) { /* ignore */ }
      }
      if (!optionNames.length) return;

      const normalizeSearch = (value) => String(value || '')
        .toLowerCase()
        .replace(/å/g, 'a')
        .replace(/ø/g, 'o')
        .replace(/æ/g, 'ae')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      const currentHandle = this.section.dataset.productHandle || '';
      const primaryPrefix = prefix.split(/\s+/)[0] || prefix;
      const translatedTerms = (value) => {
        const text = normalizeSearch(value);
        const terms = new Set([value, text]);
        const pairs = [
          [/t skjorte|tee shirt|t shirt|t-shirt/g, 'tee'],
          [/shorts?/g, 'shorts'],
          [/bukse|bukser|pants|trouser/g, 'pants'],
          [/genser|sweater|knit/g, 'sweater'],
          [/jakke|jacket/g, 'jacket'],
          [/skjorte|shirt/g, 'shirt'],
          [/tights|leggings/g, 'tights'],
          [/topp|top/g, 'top'],
        ];
        pairs.forEach(([pattern, replacement]) => {
          if (pattern.test(text)) terms.add(text.replace(pattern, replacement));
        });
        return Array.from(terms).map((term) => String(term || '').trim()).filter(Boolean);
      };
      const queryCandidates = (name) => {
        const terms = translatedTerms(name);
        const candidates = new Set();
        terms.forEach((term) => {
          candidates.add(`${prefix} ${term}`);
          candidates.add(`${primaryPrefix} ${term}`);
          candidates.add(`${term} ${primaryPrefix}`);
          candidates.add(term);
        });
        return Array.from(candidates).filter(Boolean);
      };
      const scoreProduct = (product, optionName) => {
        const haystack = normalizeSearch(`${product?.title || ''} ${product?.handle || ''} ${product?.url || ''}`);
        const optionTokens = normalizeSearch(optionName).split(' ').filter((token) => token.length > 2);
        const familyTokens = normalizeSearch(primaryPrefix).split(' ').filter((token) => token.length > 2);
        let score = 0;
        optionTokens.forEach((token) => { if (haystack.includes(token)) score += 4; });
        familyTokens.forEach((token) => { if (haystack.includes(token)) score += 5; });
        if (/set|sett|bundle|pack/.test(haystack)) score -= 12;
        if ((product?.handle || this.handleFromProductUrl(product?.url || '')) === currentHandle) score -= 100;
        return score;
      };
      const fullProductFromSearch = async (product) => {
        const handle = product?.handle || this.handleFromProductUrl(product?.url || '');
        if (!handle || handle === currentHandle) return null;
        const full = await this.fetchProductJson(handle);
        const images = Array.isArray(full?.images) ? full.images : [];
        const image = images.length
          ? images[images.length - 1]
          : (product?.image || product?.featured_image?.url || full?.featured_image || '');
        return {
          handle,
          title: full?.title || product?.title || '',
          url: `/products/${handle}`,
          image: image && String(image).startsWith('//') ? `${window.location.protocol}${image}` : image,
          price: Number(full?.price || (typeof product?.price === 'string' ? Math.round(parseFloat(product.price) * 100) : product?.price) || 0),
        };
      };

      const searchProduct = async (name) => {
        const seenHandles = new Set();
        let best = null;
        let bestScore = -Infinity;
        for (const query of queryCandidates(name)) {
          try {
            const url = `${this.cartUrl('search/suggest.json')}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`;
            const r = await fetch(url, { headers: { Accept: 'application/json' } });
            if (!r.ok) continue;
            const data = await r.json();
            const products = data?.resources?.results?.products || [];
            for (const product of products) {
              const handle = product?.handle || this.handleFromProductUrl(product?.url || '');
              if (!handle || seenHandles.has(handle) || handle === currentHandle) continue;
              seenHandles.add(handle);
              const score = scoreProduct(product, name);
              if (score > bestScore) {
                bestScore = score;
                best = product;
              }
            }
            if (bestScore >= 9) break;
          } catch (e) {
            // Try next query candidate.
          }
        }
        if (!best || bestScore < 3) return null;
        return fullProductFromSearch(best);
      };

      const results = await Promise.all(optionNames.map((name) => searchProduct(name)));
      const seen = new Set();
      const found = results.filter((product) => {
        if (!product?.handle || seen.has(product.handle)) return false;
        seen.add(product.handle);
        return true;
      });
      if (!found.length) return;

      const oldIcons = bundle.querySelector('.pdp-clean__bundle-icons');
      const oldList = bundle.querySelector('.pdp-clean__bundle-pieces');
      const list = document.createElement('ul');
      list.className = 'pdp-clean__bundle-pieces';
      list.setAttribute('role', 'list');
      list.setAttribute('aria-label', 'Produkter inkludert i settet');
      found.forEach((bp) => {
        const li = document.createElement('li');
        li.className = 'pdp-clean__bundle-piece';
        const a = document.createElement('a');
        a.className = 'pdp-clean__bundle-piece-link';
        a.href = bp.url;
        a.title = bp.title;
        a.setAttribute('aria-label', bp.title);
        if (bp.image) {
          const img = document.createElement('img');
          img.className = 'pdp-clean__bundle-piece-img';
          img.src = bp.image;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 80;
          img.height = 80;
          a.appendChild(img);
        }
        const span = document.createElement('span');
        span.className = 'pdp-clean__bundle-piece-title';
        span.textContent = bp.title;
        a.appendChild(span);
        li.appendChild(a);
        list.appendChild(li);
      });
      if (oldList) oldList.replaceWith(list);
      else if (oldIcons) oldIcons.replaceWith(list);
      else bundle.querySelector('.pdp-clean__bundle-copy')?.insertAdjacentElement('beforebegin', list);

      if (found.length !== Number(bundle.dataset.setPieces)) {
        bundle.dataset.setPieces = String(found.length);
      }
      this.updateBundlePieceUI(bundle, found);
      this.scheduleBundleMaterialHydration(found);
    }

    bindBundleMaterials() {
      if (!this.bundle) return;
      const pieces = Array.from(this.bundle.querySelectorAll('.pdp-clean__bundle-piece-link')).map((link) => ({
        title: link.getAttribute('title') || link.textContent || '',
        url: link.getAttribute('href') || '',
        handle: this.handleFromProductUrl(link.getAttribute('href') || ''),
      })).filter((piece) => piece.handle || piece.url);
      this.scheduleBundleMaterialHydration(pieces);
    }

    scheduleBundleMaterialHydration(pieces = []) {
      if (!this.bundle || !pieces.length) return;
      const run = () => this.hydrateBundleMaterials(pieces);
      if ('requestIdleCallback' in window) {
        const id = window.requestIdleCallback(run, { timeout: 2200 });
        this.unsubs.push(() => window.cancelIdleCallback?.(id));
      } else {
        const timer = window.setTimeout(run, 900);
        this.timers.push(timer);
      }
    }

    async fetchBundleMaterialText(handle) {
      const normalizedHandle = String(handle || '').trim();
      if (!normalizedHandle) return '';
      if (!this.bundleMaterialCache) this.bundleMaterialCache = new Map();
      if (this.bundleMaterialCache.has(normalizedHandle)) return this.bundleMaterialCache.get(normalizedHandle);

      const textFromHTML = (value) => String(value || '')
        .replace(/<br\s*\/?>/gi, '. ')
        .replace(/<\/(p|li|div|h[1-6])>/gi, '. ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/\s+/g, ' ')
        .trim();
      const materialFromDescription = (value) => {
        const text = textFromHTML(value);
        if (!text) return '';
        const materialWords = /(?:100\s*%|bomull|lyocell|viskose|polyester|akryl|ull|elastan|spandex|lin|modal|nylon|polyamid|rib[- ]?strikk|strikk|jersey|fleece|canvas|kordfløyel|denim|sateng|silke|mesh|velour|twill)/i;
        const sentences = (text.match(/[^.!?]+[.!?]?/g) || [text])
          .map((sentence) => sentence.replace(/\s+/g, ' ').trim())
          .filter(Boolean);
        const hits = sentences.filter((sentence) => materialWords.test(sentence));
        const picked = (hits.length ? hits : sentences.filter((sentence) => /stoff|material|kvalitet|myk/i.test(sentence))).slice(0, 2);
        return picked.join(' ').slice(0, 260).trim();
      };

      let material = '';
      try {
        const response = await fetch(this.cartUrl(`products/${normalizedHandle}`), { headers: { Accept: 'text/html' } });
        if (response.ok) {
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const row = Array.from(doc.querySelectorAll('[data-pdp-panel="materiale"] .pdp-clean__meta-row')).find((candidate) => {
            const label = normalizeSearchText(candidate.querySelector('.pdp-clean__meta-label')?.textContent || '');
            return label === 'materiale' || label === 'materialer';
          });
          material = row?.querySelector('.pdp-clean__meta-copy')?.textContent?.replace(/\s+/g, ' ').trim() || '';
        }
      } catch (e) {
        material = '';
      }

      if (!material) {
        try {
          const data = await this.fetchProductJson(normalizedHandle);
          material = materialFromDescription(data?.description || '');
        } catch (e) {
          material = '';
        }
      }

      this.bundleMaterialCache.set(normalizedHandle, material);
      return material;
    }

    async hydrateBundleMaterials(pieces = []) {
      const row = this.section.querySelector('[data-pdp-bundle-materials-row]');
      const list = this.section.querySelector('[data-pdp-bundle-materials]');
      if (!row || !list || list.dataset.pdpBundleMaterialsLoaded === 'true') return;

      const rows = await Promise.all(pieces.slice(0, 6).map(async (piece) => {
        const handle = piece.handle || this.handleFromProductUrl(piece.url || '');
        const material = await this.fetchBundleMaterialText(handle);
        if (!material) return null;
        return { title: piece.title || '', material };
      }));
      const validRows = rows.filter(Boolean);
      if (!validRows.length) return;

      list.replaceChildren(...validRows.map((piece) => {
        const item = document.createElement('li');
        item.innerHTML = `<strong>${escapeHTML(piece.title)}</strong><span>${escapeHTML(piece.material)}</span>`;
        return item;
      }));
      list.dataset.pdpBundleMaterialsLoaded = 'true';
      row.hidden = false;
    }

    updateBundlePieceUI(bundle, foundPieces) {
      const piecesCount = foundPieces.length;
      const sumComponents = foundPieces.reduce((sum, p) => sum + (Number(p.price) || 0), 0);
      // Lagre på dataset slik at updateBundle (kalt ved hver variant-change)
      // kan reberegne savings mot current variant.price
      bundle.dataset.bundleComponentSum = String(sumComponents);

      const piecesCountEl = bundle.querySelector('[data-pdp-bundle-pieces-count]');
      if (piecesCountEl) piecesCountEl.textContent = String(piecesCount);

      // Trigger initial savings-render mot current variant
      if (this.currentVariant) this.updateBundle(this.currentVariant);
    }

    bindBundleSizeSync() {
      if (!this.optionGroups || this.optionGroups.length < 2) return;
      const isBundle = !!this.section.querySelector('[data-pdp-bundle]');
      if (!isBundle) return;

      const isSizeGroup = (g) => {
        const legend = (g.querySelector('legend')?.textContent || '').toLowerCase();
        return !/farge|color|colour/.test(legend);
      };
      const sizeGroups = this.optionGroups.filter(isSizeGroup);
      if (sizeGroups.length < 2) return;

      const valuesFor = (group) => Array.from(group.querySelectorAll('[data-pdp-option-input]')).map(i => i.value);
      const firstValues = valuesFor(sizeGroups[0]);
      const sortedKey = firstValues.slice().sort().join('|');
      const allMatch = sizeGroups.every(g => valuesFor(g).sort().join('|') === sortedKey);
      if (!allMatch) return;

      const shared = document.createElement('div');
      shared.className = 'pdp-clean__bundle-shared';
      shared.setAttribute('data-pdp-bundle-shared', '');
      const pillsHtml = firstValues.map((v) => {
        const safeValue = escapeHTML(v);
        return `
          <button type="button" role="radio" aria-checked="false" class="pdp-clean__bundle-shared-pill" data-value="${safeValue}">
            <span class="pdp-clean__bundle-shared-pill-value">${safeValue}</span>
            <span class="pdp-clean__bundle-shared-pill-status" data-pdp-bundle-shared-pill-status hidden></span>
          </button>
        `;
      }).join('');
      shared.innerHTML = `
        <div class="pdp-clean__bundle-shared-head">
          <span class="pdp-clean__bundle-shared-label">Velg én størrelse for hele settet</span>
          <button type="button" class="pdp-clean__bundle-shared-mix" data-pdp-bundle-mix aria-label="Miks størrelser og velg størrelse per plagg">
            <span>Miks størrelser</span>
            <span aria-hidden="true">Velg per plagg →</span>
          </button>
        </div>
        <div class="pdp-clean__bundle-shared-pills" role="radiogroup" aria-label="Felles størrelse">${pillsHtml}</div>
        <p class="pdp-clean__bundle-shared-note" data-pdp-bundle-shared-note role="status" hidden></p>
      `;
      this.optionGroups[0].insertAdjacentElement('beforebegin', shared);

      this.bundleShared = shared;
      this.bundleSharedSizeGroups = sizeGroups;
      sizeGroups.forEach((g) => g.hidden = true);

      const sharedPills = Array.from(shared.querySelectorAll('.pdp-clean__bundle-shared-pill'));
      const mixToggle = shared.querySelector('[data-pdp-bundle-mix]');
      this.updateBundleSharedState();

      sharedPills.forEach((pill) => {
        this.on(pill, 'click', () => {
          if (pill.disabled) return;
          const value = pill.dataset.value;
          sharedPills.forEach((p) => {
            const selected = p === pill;
            p.classList.toggle('is-selected', selected);
            p.setAttribute('aria-checked', selected ? 'true' : 'false');
          });
          if (pill.dataset.soldout === 'true' || pill.dataset.impossible === 'true') this.showBundleSharedNote(pill);
          else this.hideBundleSharedNote();
          sizeGroups.forEach((group) => {
            const match = group.querySelector(`[data-pdp-option-input][value="${cssEscape(value)}"]`);
            if (match && !match.disabled) {
              match.checked = true;
              match.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        });
      });

      this.on(mixToggle, 'click', () => {
        shared.hidden = true;
        sizeGroups.forEach((g) => g.hidden = false);
        this.hideBundleSharedNote();
        this.updateVariantStatus(this.currentVariant);
      });
    }

    updateBundleSharedState() {
      const shared = this.bundleShared || this.section.querySelector('[data-pdp-bundle-shared]');
      const sizeGroups = this.bundleSharedSizeGroups || [];
      if (!shared || !sizeGroups.length) return;

      shared.querySelectorAll('.pdp-clean__bundle-shared-pill').forEach((pill) => {
        const value = pill.dataset.value || '';
        const info = this.bundleSharedSizeInfo(value, sizeGroups);
        const copy = this.bundleSharedStatusCopy(value, info);
        const soldOut = info.exactExists && !info.exactAvailable;
        const impossible = !info.exactExists;
        const status = pill.querySelector('[data-pdp-bundle-shared-pill-status]');

        pill.classList.toggle('is-unavailable', soldOut || impossible);
        pill.dataset.soldout = soldOut ? 'true' : 'false';
        pill.dataset.impossible = impossible ? 'true' : 'false';
        pill.dataset.canMix = info.canMix ? 'true' : 'false';
        pill.dataset.statusTitle = copy.title || '';
        pill.dataset.statusCopy = copy.copy || '';
        pill.title = copy.title && copy.copy ? `${copy.title}. ${copy.copy}` : (copy.title || '');
        pill.disabled = impossible;
        pill.setAttribute('aria-disabled', impossible ? 'true' : 'false');

        if (status) {
          status.textContent = soldOut ? 'Utsolgt' : (impossible ? 'Ikke mulig' : '');
          status.hidden = !(soldOut || impossible);
        }

        if (pill.classList.contains('is-selected')) {
          if (soldOut || impossible) this.showBundleSharedNote(pill);
          else this.hideBundleSharedNote();
        }
      });
    }

    bindGalleryZoom() {
      // High-performance PDP lightbox:
      // - open instantly with the already-loaded product image
      // - swap to decoded high-res image when ready
      // - preload neighbor images so next/prev feels immediate
      // - one transform pipeline for zoom/pan to avoid mousemove jank
      if (!this.gallery) return;
      const getItems = () => Array.from(this.gallery.querySelectorAll('.pdp-clean__gallery-item:not([data-pdp-gallery-loop-clone])'));
      const items = getItems();
      if (!items.length) return;

      let lightbox = null, lbImg = null, lbCounter = null, lbCloseBtn = null;
      let stage = null;
      let currentIdx = 0, lastFocused = null;
      let loadToken = 0;
      let raf = null;
      let scale = 1;
      let pan = { x: 0, y: 0 };
      let ignoreClickUntil = 0;
      let galleryPointer = null;
      let pendingGalleryZoomItem = null;
      let suppressGalleryZoomUntil = 0;
      const ZOOM = 2.35;
      const MAX_ZOOM = 3.2;
      const preloadCache = new Map();

      const normalizedIndex = (idx) => {
        const count = getItems().length || 1;
        return (idx + count) % count;
      };

      const fullSrcFor = (img) => {
        const src = img?.currentSrc || img?.src || '';
        if (!src) return '';
        try {
          const url = new URL(src, window.location.href);
          if (url.searchParams.has('width')) {
            url.searchParams.set('width', '2200');
            return url.toString();
          }
        } catch (error) { /* fall through to string replacement */ }
        return src
          .replace(/width=\d+/i, 'width=2200')
          .replace(/_(\d{2,4})x(\d{0,4})?(\.|@)/, '_2200x$3');
      };

      const imageData = (idx) => {
        const list = getItems();
        const item = list[normalizedIndex(idx)];
        const img = item?.querySelector('img');
        const thumb = img?.currentSrc || img?.src || img?.getAttribute('src') || '';
        return {
          alt: img?.alt || '',
          full: fullSrcFor(img) || thumb,
          thumb,
        };
      };

      const preloadImage = (idx, priority = false) => {
        const data = imageData(idx);
        if (!data.full) return Promise.resolve('');
        const cached = preloadCache.get(data.full);
        if (cached) {
          if (priority && cached.image?.fetchPriority) cached.image.fetchPriority = 'high';
          return cached.promise;
        }

        const probe = new Image();
        probe.decoding = 'async';
        probe.loading = 'eager';
        if ('fetchPriority' in probe) probe.fetchPriority = priority ? 'high' : 'low';

        const record = { image: probe, loaded: false, promise: null };
        record.promise = new Promise((resolve) => {
          probe.onload = () => resolve(data.full);
          probe.onerror = () => resolve(data.full);
          probe.src = data.full;
          if (probe.decode) {
            probe.decode().then(() => resolve(data.full)).catch(() => resolve(data.full));
          }
        }).then((src) => {
          record.loaded = true;
          return src;
        });
        preloadCache.set(data.full, record);
        return record.promise;
      };

      const warmVisibleImages = () => {
        [0, 1, 2].forEach((idx) => preloadImage(idx, idx === 0));
      };
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warmVisibleImages, { timeout: 2500 });
      } else {
        window.setTimeout(warmVisibleImages, 1400);
      }

      const zoomBlockedSelector = [
        'a',
        'button',
        'input',
        'select',
        'textarea',
        'summary',
        '[contenteditable="true"]',
        '[data-pdp-model-toggle]',
        '[data-pdp-gallery-prev]',
        '[data-pdp-gallery-next]',
        '.pdp-clean__wishlist-slot',
      ].join(',');

      const itemFromZoomEvent = (event) => {
        if (!(event.target instanceof Element)) return null;
        const item = event.target.closest('.pdp-clean__gallery-item');
        if (!item || !this.gallery.contains(item)) return null;
        const blocked = event.target.closest(zoomBlockedSelector);
        if (blocked && blocked !== item && !blocked.classList.contains('pdp-clean__gallery-item')) return null;
        return item;
      };

      const clampPan = () => {
        if (!stage || !lbImg || scale <= 1) {
          pan = { x: 0, y: 0 };
          return;
        }
        const stageRect = stage.getBoundingClientRect();
        const baseW = lbImg.offsetWidth || lbImg.getBoundingClientRect().width;
        const baseH = lbImg.offsetHeight || lbImg.getBoundingClientRect().height;
        const maxX = Math.max(0, ((baseW * scale) - stageRect.width) / 2);
        const maxY = Math.max(0, ((baseH * scale) - stageRect.height) / 2);
        pan.x = Math.max(-maxX, Math.min(maxX, pan.x));
        pan.y = Math.max(-maxY, Math.min(maxY, pan.y));
      };

      const applyTransform = (withTransition = true) => {
        if (!lbImg) return;
        clampPan();
        if (withTransition) {
          lbImg.classList.remove('is-dragging');
        } else {
          lbImg.classList.add('is-dragging');
        }
        window.cancelAnimationFrame(raf);
        raf = window.requestAnimationFrame(() => {
          if (!lbImg) return;
          lbImg.classList.toggle('is-zoomed', scale > 1);
          lbImg.style.transform = scale > 1
            ? `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${scale})`
            : '';
        });
      };

      const resetZoom = () => {
        scale = 1;
        pan = { x: 0, y: 0 };
        if (lbImg) {
          lbImg.classList.remove('is-zoomed', 'is-dragging');
          lbImg.style.transform = '';
        }
      };

      const zoomAt = (x, y) => {
        if (!lbImg) return;
        if (scale > 1) {
          resetZoom();
          return;
        }
        const rect = lbImg.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const dx = x - (rect.left + rect.width / 2);
        const dy = y - (rect.top + rect.height / 2);
        scale = ZOOM;
        pan = {
          x: -dx * (ZOOM - 1),
          y: -dy * (ZOOM - 1),
        };
        applyTransform(true);
      };

      const ensureLightbox = () => {
        if (lightbox) return;
        lightbox = document.createElement('div');
        lightbox.className = 'pdp-clean__lightbox';
        lightbox.setAttribute('role', 'dialog');
        lightbox.setAttribute('aria-modal', 'true');
        lightbox.setAttribute('aria-label', 'Bildevisning');
        lightbox.tabIndex = -1;
        lightbox.innerHTML = `
          <button type="button" class="pdp-clean__lightbox-close" aria-label="Lukk">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M5 5l14 14M19 5L5 19"/></svg>
          </button>
          <button type="button" class="pdp-clean__lightbox-prev" aria-label="Forrige bilde">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6l-6 6 6 6"/></svg>
          </button>
          <button type="button" class="pdp-clean__lightbox-next" aria-label="Neste bilde">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>
          </button>
          <div class="pdp-clean__lightbox-stage">
            <img class="pdp-clean__lightbox-img" alt="" />
          </div>
          <div class="pdp-clean__lightbox-counter" aria-live="polite"></div>
        `;
        document.body.appendChild(lightbox);
        this.lightbox = lightbox;
        lbImg = lightbox.querySelector('.pdp-clean__lightbox-img');
        lbCounter = lightbox.querySelector('.pdp-clean__lightbox-counter');
        lbCloseBtn = lightbox.querySelector('.pdp-clean__lightbox-close');
        stage = lightbox.querySelector('.pdp-clean__lightbox-stage');

        this.on(lbCloseBtn, 'click', close);
        this.on(lightbox.querySelector('.pdp-clean__lightbox-prev'), 'click', prev);
        this.on(lightbox.querySelector('.pdp-clean__lightbox-next'), 'click', next);
        this.on(lightbox, 'click', (e) => { if (e.target === lightbox) close(); });

        this.on(stage, 'click', (e) => {
          if (e.target !== lbImg && e.target !== stage) return;
          if (performance.now() < ignoreClickUntil) return;
          zoomAt(e.clientX, e.clientY);
        });

        let pointerId = null;
        let pointerStart = { x: 0, y: 0 };
        let panStart = { x: 0, y: 0 };
        let moved = false;

        this.on(stage, 'pointerdown', (e) => {
          if (e.target.closest('button')) return;
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          pointerId = e.pointerId;
          pointerStart = { x: e.clientX, y: e.clientY };
          panStart = { ...pan };
          moved = false;
          stage.setPointerCapture?.(e.pointerId);
          if (scale > 1) e.preventDefault();
        }, { passive: false });

        this.on(stage, 'pointermove', (e) => {
          if (pointerId !== e.pointerId) return;
          const dx = e.clientX - pointerStart.x;
          const dy = e.clientY - pointerStart.y;
          if (Math.abs(dx) > 4 || Math.abs(dy) > 4) moved = true;
          if (scale <= 1) return;
          e.preventDefault();
          pan = { x: panStart.x + dx, y: panStart.y + dy };
          applyTransform(false);
        }, { passive: false });

        this.on(stage, 'pointerup', (e) => {
          if (pointerId !== e.pointerId) return;
          const dx = e.clientX - pointerStart.x;
          const dy = e.clientY - pointerStart.y;
          pointerId = null;
          stage.releasePointerCapture?.(e.pointerId);
          lbImg.classList.remove('is-dragging');
          if (moved) ignoreClickUntil = performance.now() + 260;
          if (scale <= 1 && Math.abs(dx) > 54 && Math.abs(dx) > Math.abs(dy) * 1.45) {
            ignoreClickUntil = performance.now() + 320;
            dx < 0 ? next() : prev();
            return;
          }
          applyTransform(true);
        });

        this.on(stage, 'pointercancel', (e) => {
          if (pointerId !== e.pointerId) return;
          pointerId = null;
          lbImg.classList.remove('is-dragging');
          applyTransform(true);
        });

        this.on(lightbox, 'wheel', (e) => {
          if (!lightbox.classList.contains('is-open')) return;
          e.preventDefault();
          const delta = e.deltaY < 0 ? 0.18 : -0.18;
          const nextScale = Math.max(1, Math.min(MAX_ZOOM, scale + delta));
          if (nextScale === 1) {
            resetZoom();
            return;
          }
          if (scale === 1) {
            const rect = lbImg.getBoundingClientRect();
            pan = {
              x: -(e.clientX - (rect.left + rect.width / 2)) * (nextScale - 1),
              y: -(e.clientY - (rect.top + rect.height / 2)) * (nextScale - 1),
            };
          }
          scale = nextScale;
          applyTransform(false);
        }, { passive: false });
      };

      const show = (idx) => {
        ensureLightbox();
        const count = getItems().length;
        if (!count) return;
        currentIdx = (idx + count) % count;
        const token = ++loadToken;
        const data = imageData(currentIdx);
        if (!data.thumb && !data.full) return;
        resetZoom();
        lbImg.alt = data.alt;
        if (data.thumb && lbImg.src !== data.thumb) lbImg.src = data.thumb;
        lightbox.classList.add('is-loading');
        lbCounter.textContent = `${currentIdx + 1} / ${count}`;
        lightbox.classList.add('is-open');
        document.documentElement.style.overflow = 'hidden';
        if (lbCloseBtn) window.requestAnimationFrame(() => lbCloseBtn.focus());

        const cached = preloadCache.get(data.full);
        const setLoaded = () => lightbox?.classList.remove('is-loading');
        if (cached?.loaded) {
          if (lbImg.src !== data.full) lbImg.src = data.full;
          setLoaded();
        } else {
          preloadImage(currentIdx, true).then((fullSrc) => {
            if (token !== loadToken || currentIdx !== normalizedIndex(idx) || !lightbox?.classList.contains('is-open')) return;
            if (fullSrc && lbImg.src !== fullSrc) lbImg.src = fullSrc;
            const done = () => {
              if (token === loadToken) setLoaded();
            };
            if (lbImg.decode) lbImg.decode().then(done).catch(done);
            else done();
          });
        }
        preloadImage(currentIdx + 1);
        preloadImage(currentIdx - 1);
      };
      const showItem = (item) => {
        const list = getItems();
        const idx = list.indexOf(item);
        if (idx < 0) return;
        lastFocused = item;
        show(idx);
      };
      const close = () => {
        if (!lightbox) return;
        lightbox.classList.remove('is-open');
        lightbox.classList.remove('is-loading');
        resetZoom();
        document.documentElement.style.overflow = '';
        if (lastFocused?.focus) lastFocused.focus();
      };
      const prev = () => show(currentIdx - 1);
      const next = () => show(currentIdx + 1);

      this.on(this.gallery, 'pointerdown', (event) => {
        const item = itemFromZoomEvent(event);
        if (!item || event.button !== 0) return;
        galleryPointer = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          item,
        };
        pendingGalleryZoomItem = item;
      }, { capture: true, passive: true });

      this.on(this.gallery, 'pointerup', (event) => {
        if (!galleryPointer || galleryPointer.id !== event.pointerId) return;
        const dx = event.clientX - galleryPointer.x;
        const dy = event.clientY - galleryPointer.y;
        galleryPointer = null;
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          pendingGalleryZoomItem = null;
          suppressGalleryZoomUntil = performance.now() + 260;
        }
      }, { capture: true, passive: true });

      this.on(this.gallery, 'pointercancel', () => {
        galleryPointer = null;
        pendingGalleryZoomItem = null;
        suppressGalleryZoomUntil = performance.now() + 260;
      }, { capture: true, passive: true });

      this.on(this.gallery, 'click', (event) => {
        if (event.defaultPrevented || performance.now() < suppressGalleryZoomUntil) return;
        const item = itemFromZoomEvent(event) || pendingGalleryZoomItem;
        pendingGalleryZoomItem = null;
        if (!item) return;
        event.__pdpZoomHandled = true;
        showItem(item);
      }, { capture: true });

      items.forEach((item, idx) => {
        item.style.cursor = 'zoom-in';
        item.tabIndex = window.matchMedia('(min-width: 990px)').matches && !item.classList.contains('is-active') ? -1 : 0;
        item.setAttribute('role', 'button');
        item.setAttribute('aria-label', `Forstørr bilde ${idx + 1} av ${items.length}`);
        this.on(item, 'mouseenter', () => preloadImage(idx, true), { passive: true });
        this.on(item, 'focus', () => preloadImage(idx, true), { passive: true });
        this.on(item, 'click', (e) => {
          if (e.defaultPrevented || e.__pdpZoomHandled || performance.now() < suppressGalleryZoomUntil) return;
          showItem(item);
        });
        this.on(item, 'keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          showItem(item);
        });
      });

      this.on(document, 'keydown', (e) => {
        if (!lightbox || !lightbox.classList.contains('is-open')) return;
        if (e.key === 'Escape') { e.preventDefault(); close(); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
        else if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          scale = Math.min(MAX_ZOOM, scale > 1 ? scale + 0.35 : ZOOM);
          applyTransform(true);
        } else if (e.key === '-') {
          e.preventDefault();
          scale = Math.max(1, scale - 0.35);
          if (scale === 1) resetZoom();
          else applyTransform(true);
        }
      });

      // Cleanup på destroy
      this.unsubs.push(() => {
        window.cancelAnimationFrame(raf);
        if (lightbox) {
          lightbox.remove();
          lightbox = null;
          this.lightbox = null;
        }
        document.documentElement.style.overflow = '';
      });
    }

    async bindBundleParent() {
      // Reverse-lookup: hvis dette IKKE er et bundle-produkt, sjekk om det er
      // KOMPONENT i et bundle. Hvis ja: render en liten, tydelig sett-upsell
      // under pris, uten at den konkurrerer med hoved-ATC.
      if (this.section.querySelector('[data-pdp-bundle]')) return; // selve bundlen
      const productId = this.section.dataset.productId;
      if (!productId) return;

      // Hent produkt-JSON for å få tags
      let tags = [];
      let productTitle = '';
      let productPrice = 0;
      try {
        const r = await fetch(this.cartUrl(`products/${this.section.dataset.productHandle}.js`));
        const data = await r.json();
        tags = (data?.tags || []).map(t => String(t).toLowerCase());
        productTitle = data?.title || '';
        productPrice = data?.variants?.[0]?.price || 0;
      } catch (e) { return; }
      if (!tags.length) return;

      // Color/style-tagger: ord som finnes både på komponent og bundle
      // Generiske ord vi vil IKKE matche på
      const genericTags = new Set(['bundle', 'set', 'sett', 'bestselger', 'workout', 'yoga', 'trening', 'treningsklær', 'treningssett', 'klær', 'top', 'tights', 'jacket', 'sale', 'salg', 'beauty', 'selvpleie', 'kommer-snart', 'presale', 'final-sale']);
      const styleTags = tags.filter(t => !genericTags.has(t) && t.length > 2);
      if (!styleTags.length) return;

      // Strikt matching: bundle-tittel MÅ inneholde komponentens DISTINKTE prefix-ord
      // (første eller siste ord i komponent-tittel, ikke generiske kategori-ord).
      // Forhindrer "Knit Loungewear Set Espresso" fra å matche "Chunky Knit Sweater" via "knit".
      const titleWords = (productTitle || '').split(/[\s-]+/).filter(Boolean);
      // Hopp over generiske ord (tags, sale, etc) når vi velger color/style-prefix
      const skipWords = new Set(['knit','strikk','tops','genser','gensere','sweater','top','tights','jacket','pants','sale','salg','laley','the','og','og,','set','sett','bundle','beauty','selvpleie']);
      const distinctWords = titleWords
        .map(w => w.toLowerCase().replace(/[^a-zæøå0-9_-]/g, ''))
        .filter(w => w.length > 2 && !skipWords.has(w));
      if (!distinctWords.length) return;

      let candidate = null;
      const hasDistinctMatch = (p) => {
        const haystack = `${p.title || ''} ${p.handle || (p.url || '').split('?')[0].split('/').pop() || ''}`.toLowerCase();
        return distinctWords.some(w => haystack.includes(w));
      };
      // Søk fra hver distinkt ord som har størst sjanse for å være color/style marker
      for (const word of distinctWords) {
        const url = `${this.cartUrl('search/suggest.json')}?q=${encodeURIComponent(word + ' set')}&resources[type]=product&resources[limit]=5`;
        try {
          const r = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!r.ok) continue;
          const data = await r.json();
          const products = data?.resources?.results?.products || [];
          for (const p of products) {
            if (String(p.id || p.product_id || '') === String(productId)) continue;
            if (!/\b(set|sett|bundle|pack|gavesett)\b/i.test(p.title)) continue;
            if (!hasDistinctMatch(p)) continue;
            candidate = p;
            break;
          }
          if (candidate) break;
        } catch (e) { /* continue */ }
      }
      if (!candidate) return;

      // Bundle-pris (min variant fra candidate)
      const bundlePriceCents = typeof candidate.price === 'string'
        ? Math.round(parseFloat(candidate.price) * 100)
        : candidate.price;

      // Beregn savings: hent komponentene fra bundle (via samme pattern som bundle-PDP)
      // og sum opp deres priser, sammenlign med bundle min-pris.
      let savingsCents = 0;
      try {
        // candidate.url har ofte tracking-query — strip før vi appender .js
        const bundlePath = (candidate.url || '').split('?')[0];
        const bundleJson = await fetch(`${bundlePath}.js`).then(r => r.ok ? r.json() : null);
        const bundleOptions = (bundleJson?.options || []).map(o => typeof o === 'string' ? o : o?.name).filter(Boolean);
        const bundleTitleWords = (bundleJson?.title || candidate.title || '').split(' ');
        const drops = ['set', 'sett', 'pack', 'bundle', 'workout', 'yoga', 'training'];
        const prefixParts = [];
        for (const w of bundleTitleWords) {
          if (drops.includes(w.toLowerCase())) break;
          prefixParts.push(w);
        }
        const bundlePrefix = prefixParts.join(' ');

        if (bundleOptions.length > 0 && bundlePrefix) {
          const compResults = await Promise.all(bundleOptions.map(async (name) => {
            const url = `${this.cartUrl('search/suggest.json')}?q=${encodeURIComponent(bundlePrefix + ' ' + name)}&resources[type]=product&resources[limit]=1`;
            const r = await fetch(url, { headers: { Accept: 'application/json' } });
            if (!r.ok) return 0;
            const data = await r.json();
            const p = data?.resources?.results?.products?.[0];
            if (!p) return 0;
            const pr = typeof p.price === 'string' ? Math.round(parseFloat(p.price) * 100) : p.price;
            return Number(pr) || 0;
          }));
          const sumCents = compResults.reduce((s, v) => s + v, 0);
          if (sumCents > bundlePriceCents) {
            savingsCents = sumCents - bundlePriceCents;
          }
        }
      } catch (e) { /* swallow */ }

      // Render parent-bundle-card after the primary buy flow.
      const priceWrap = this.section.querySelector('[data-pdp-price-wrap]');
      const deliveryStrip = this.section.querySelector('[data-pdp-delivery-strip]');
      const visibleBis = this.bis && !this.bis.hidden ? this.bis : null;
      const buyFlowAnchor = visibleBis || deliveryStrip || this.form || priceWrap;
      if (!buyFlowAnchor) return;
      if (this.section.querySelector('[data-pdp-bundle-parent]')) return;

      const card = document.createElement('aside');
      card.className = 'pdp-clean__bundle-parent';
      card.setAttribute('data-pdp-bundle-parent', '');
      const candidateTitle = escapeHTML(candidate.title);
      const candidateUrl = escapeHTML(candidate.url);
      const candidateImage = candidate.image ? escapeHTML(candidate.image) : '';
      const bundleSavingsText = savingsCents > 0 ? this.money(savingsCents) : '';
      const savingsBadge = savingsCents > 0
        ? `<span class="pdp-clean__bundle-parent-save">Spar ${bundleSavingsText}</span>`
        : '';
      const ariaSavings = savingsCents > 0 ? ` og spar ${bundleSavingsText}` : '';
      card.innerHTML = `
        <a class="pdp-clean__bundle-parent-link" href="${candidateUrl}" aria-label="Velg ${candidateTitle}${ariaSavings}">
          ${candidateImage ? `<img class="pdp-clean__bundle-parent-img" src="${candidateImage}" alt="" loading="lazy" width="42" height="54">` : ''}
          <span class="pdp-clean__bundle-parent-copy">
            <span class="pdp-clean__bundle-parent-meta">
              <span class="pdp-clean__bundle-parent-label">Kjøp som sett</span>
              ${savingsBadge}
            </span>
            <span class="pdp-clean__bundle-parent-title">${candidateTitle}</span>
          </span>
          <span class="pdp-clean__bundle-parent-cta" aria-hidden="true">Velg sett</span>
        </a>
      `;
      buyFlowAnchor.insertAdjacentElement('afterend', card);
    }

    bindBisObserver() {
      // Klaviyo injekter trigger asynkront — observer både section + form
      // (form refereres ikke alltid før Klaviyo hydrerer).
      if (!this.section) return;
      let raf = null;
      const schedule = () => {
        if (raf) return;
        raf = window.requestAnimationFrame(() => {
          raf = null;
          this.updateBis(this.currentVariant);
        });
      };
      const observer = new MutationObserver(schedule);
      observer.observe(this.section, { childList: true, subtree: true });
      this.unsubs.push(() => {
        if (raf) window.cancelAnimationFrame(raf);
        observer.disconnect();
      });
    }

    updateBundle(variant) {
      if (!this.bundle || !variant) return;
      // Variant-pris oppdateres slik at savings beregnes mot riktig pris
      this.bundle.dataset.bundlePrice = variant.price;

      // Manuell compare-cents fra metafelt vinner over auto-discover sum
      // (lar merchant overstyre Shopify Bundles auto-pris-syncing)
      const manualCompare = Number(this.bundle.dataset.bundleCompareCents || 0);
      const autoSum = Number(this.bundle.dataset.bundleComponentSum || 0);
      const reference = manualCompare > 0 ? manualCompare : autoSum;
      const savings = reference > 0 ? (reference - variant.price) : 0;

      // Bundle headline under pris: bekreft settverdi uten å gjøre det til en alternativ CTA.
      const headline = this.section.querySelector('[data-pdp-bundle-headline]');
      const headlineAmount = this.section.querySelector('[data-pdp-bundle-headline-amount]');
      if (headline && headlineAmount) {
        if (savings > 0) {
          headlineAmount.textContent = this.money(savings);
          headline.replaceChildren(document.createTextNode('Settpris · du sparer '), headlineAmount);
          headline.hidden = false;
        } else {
          headline.hidden = true;
        }
      }
      // Bundle-copy: vis kun pieces-count (savings vises i headline øverst, ingen duplikater)
      const copyEl = this.bundle.querySelector('[data-pdp-bundle-copy]');
      const piecesCount = Number(this.bundle.dataset.setPieces || 0);
      if (copyEl) {
        if (savings > 0) {
          // Skjul duplikat-tekst når savings-headline allerede vises
          copyEl.hidden = true;
        } else {
          copyEl.hidden = false;
          copyEl.innerHTML = `<span data-pdp-bundle-pieces-count>${piecesCount}</span> deler inkludert`;
        }
      }
    }

    updateStickyContent(variant) {
      if (this.stickyPrice && variant) this.stickyPrice.textContent = this.money(variant.price);
      if (this.stickyButton) {
        // Sticky-knappen speiler hoved-ATC: velg størrelse først, kjøp når varianten er valgt.
        if (!this.hasRequiredSizeSelection()) {
          this.stickyButton.disabled = false;
          this.stickyButton.dataset.mode = 'pick';
          this.stickyButton.textContent = 'Velg størrelse';
        } else if (!variant) {
          this.stickyButton.disabled = true;
          this.stickyButton.dataset.mode = 'unavailable';
          this.stickyButton.textContent = 'Velg alternativer';
        } else if (!variant.available) {
          // Utsolgt valgt størrelse → speil hoved-ATC sin BIS-state (klikkbar varsling).
          // Klikk proxyer til form.requestSubmit(atc) som kjører eksisterende BIS-flow.
          this.stickyButton.disabled = false;
          this.stickyButton.dataset.mode = 'bis';
          this.stickyButton.textContent = 'Varsle';
        } else {
          this.stickyButton.disabled = false;
          this.stickyButton.dataset.mode = 'add';
          this.stickyButton.textContent = 'Kjøp';
        }
      }
      if (this.stickyImg && variant?.featuredImage) {
        this.stickyImg.src = variant.featuredImage;
        this.stickyImg.alt = variant.featuredImageAlt || '';
      }
      this.updateStickyVisibility();
    }

    updateUrl(variant) {
      if (!window.history?.replaceState || !variant?.id) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({ path: url.toString() }, '', url.toString());
    }

    syncGalleryToVariant(variant, behavior = 'smooth') {
      if (!this.gallery) return;

      this.gallery.querySelectorAll('[data-pdp-gallery-loop-clone]').forEach((clone) => clone.remove());
      const items = Array.from(this.gallery.querySelectorAll('.pdp-clean__gallery-item:not([data-pdp-gallery-loop-clone])'))
        .sort((a, b) => Number(a.dataset.originalIndex || 0) - Number(b.dataset.originalIndex || 0));
      const featured = variant?.featuredMediaId
        ? items.find((item) => String(item.dataset.mediaId) === String(variant.featuredMediaId))
        : null;

      const orderedItems = featured ? [featured, ...items.filter((item) => item !== featured)] : items;
      orderedItems.forEach((item) => this.gallery.appendChild(item));

      this.prepareGalleryLoop?.();
      if (this.refreshGalleryStage) this.refreshGalleryStage(0, behavior);
      else if (window.matchMedia('(max-width: 749px)').matches) this.gallery.scrollTo({ left: 0, behavior });
      this.requestGalleryProgressUpdate?.();
    }

    bindForm() {
      if (!this.form || !this.atc) return;
      // Router basert på data-mode:
      //   "add"  → dispatch theme:cart:add (Laley-temaet håndterer AJAX + drawer)
      //   "bis"  → click Klaviyos BIS-trigger (eller fallback: åpne email-modal)
      //   andre → no-op
      this.on(this.form, 'submit', (event) => {
        event.preventDefault();
        this.clearError();
        const mode = this.atc.dataset.mode;
        if (mode === 'pick') {
          // CP-stil: klikk på ATC før str er valgt → vis inline-feil + scroll/fokuser picker.
          // ATC har allerede "Legg i handlekurv — X kr"-tekst, så feilmeldingen forklarer hva som mangler.
          this.showError('Velg størrelse for å fortsette');
          const dropdowns = this.section.querySelectorAll('[data-pdp-size-group] [data-pdp-dropdown]');
          dropdowns.forEach((d) => { d.open = true; });
          const sharedPicker = this.section.querySelector('[data-pdp-bundle-shared]:not([hidden])');
          const sizeGroup = this.section.querySelector('[data-pdp-size-group]')
            || this.optionGroups.find((group) => !group.querySelector('.pdp-clean__option--swatch'));
          const target = sharedPicker || sizeGroup || this.optionGroups[0];
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const focusEl = target.querySelector('button, .pdp-clean__dropdown-trigger, [data-pdp-option-input]');
            if (focusEl) focusEl.focus();
          }
          return;
        }
        if (mode === 'add') {
          document.dispatchEvent(new CustomEvent('theme:cart:add', {
            detail: { button: this.atc },
            bubbles: false,
          }));
        } else if (mode === 'bis') {
          // Klaviyo injekter sin egen trigger i form eller andre steder.
          // Vi klikker den hvis vi finner den — Klaviyo åpner sin BIS-modal.
          const trigger = document.querySelector('.klaviyo-bis-trigger');
          if (trigger) {
            trigger.click();
          } else {
            // Fallback: dispatch event som tema/admin kan lytte på
            document.dispatchEvent(new CustomEvent('theme:product:bis-request', {
              detail: { variant: this.currentVariant, productId: this.section.dataset.productId },
              bubbles: true,
            }));
            // Som siste fallback: hjelp brukeren til kontakt
            const variantLabel = this.cleanVariantLabel(this.currentVariant);
            const subjectTitle = variantLabel ? `${this.productTitle()} - ${variantLabel}` : this.productTitle();
            const subject = encodeURIComponent(`Si fra når ${subjectTitle} er tilbake`);
            window.location.href = `mailto:hei@laley.no?subject=${subject}`;
          }
        }
      });
    }

    bindOutfitAdds() {
      // Hver outfit-knapp er nå type="submit" inni sin egen mini-form med
      // hidden id+quantity. Submit går direkte til cart/add.js uten modal.
      this.section.querySelectorAll('[data-pdp-outfit-form]').forEach((form) => {
        this.bindOutfitForm(form, false);
      });
    }

    clearError() {
      if (!this.error) return;
      this.error.textContent = '';
      this.error.hidden = true;
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    bindTabs() {
      // Horisontale faner (på linje, samme størrelse) — desktop + mobil.
      const root = this.section.querySelector('[data-pdp-tabs]') || this.section.querySelector('[data-pdp-accordions]');
      if (!root) return;
      const tabs = Array.from(root.querySelectorAll('[data-pdp-tab]'));
      const panels = Array.from(root.querySelectorAll('[data-pdp-panel]'));
      if (!tabs.length) return;

      const activate = (key, opts) => {
        const focus = opts && opts.focus;
        tabs.forEach((t) => {
          const on = t.dataset.pdpTab === key;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          if (on && focus) t.focus();
        });
        panels.forEach((p) => {
          const on = p.dataset.pdpPanel === key;
          p.classList.toggle('is-active', on);
          p.setAttribute('aria-hidden', on ? 'false' : 'true');
        });
      };

      tabs.forEach((tab) => {
        this.on(tab, 'click', () => activate(tab.dataset.pdpTab));
        // WAI-ARIA tabs: pil venstre/høyre bytter fane
        this.on(tab, 'keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          const i = tabs.indexOf(tab);
          const dir = e.key === 'ArrowRight' ? 1 : -1;
          const next = tabs[(i + dir + tabs.length) % tabs.length];
          activate(next.dataset.pdpTab, { focus: true });
        });
      });

      // Hook for review proof links/cards. Reviews may be a lower standalone
      // section now; only activate the tab when that tab actually exists.
      const reviewTab = tabs.find((tab) => tab.dataset.pdpTab === 'anmeldelser');
      this.activateReviewsTab = () => {
        if (reviewTab) activate('anmeldelser');
      };
    }

    bindGallery() {
      const isDesktopStage = () => window.matchMedia('(min-width: 990px)').matches;
      const prevButton = this.section.querySelector('[data-pdp-gallery-prev]');
      const nextButton = this.section.querySelector('[data-pdp-gallery-next]');
      const LOOP_CLONE_SELECTOR = '[data-pdp-gallery-loop-clone="true"]';
      const REAL_ITEM_SELECTOR = `.pdp-clean__gallery-item:not(${LOOP_CLONE_SELECTOR})`;
      const LOOP_BUFFER_MAX = 3;
      const SCROLL_SETTLE_MS = 140;
      const SCROLL_SNAP_TOLERANCE = 2;
      let galleryItemCount = 0;

      const allGalleryItems = () => this.gallery
        ? Array.from(this.gallery.querySelectorAll('.pdp-clean__gallery-item'))
        : [];
      const galleryItems = () => this.gallery
        ? Array.from(this.gallery.querySelectorAll(REAL_ITEM_SELECTOR))
        : [];
      const refreshGalleryCount = () => {
        if (!this.gallery) return;
        galleryItemCount = galleryItems().length;
      };

      const itemRealIndex = (item) => {
        const parsed = Number(item?.dataset?.pdpGalleryRealIndex);
        return Number.isFinite(parsed) ? parsed : -1;
      };
      const loopBufferSize = (count) => count > 1 ? Math.min(LOOP_BUFFER_MAX, count) : 0;

      const prepareClone = (source, realIndex) => {
        const clone = source.cloneNode(true);
        clone.dataset.pdpGalleryLoopClone = 'true';
        clone.dataset.pdpGalleryRealIndex = String(realIndex);
        clone.classList.remove('is-active');
        clone.setAttribute('aria-hidden', 'true');
        clone.tabIndex = -1;
        try { clone.inert = true; } catch (error) { /* inert is progressive enhancement */ }
        clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        clone.querySelectorAll('a, button, input, select, textarea, summary, [tabindex], [role="button"]').forEach((el) => {
          el.tabIndex = -1;
          el.setAttribute('aria-hidden', 'true');
        });
        return clone;
      };
      const cloneFragment = (items) => {
        const fragment = document.createDocumentFragment();
        items.forEach((item) => fragment.appendChild(prepareClone(item, itemRealIndex(item))));
        return fragment;
      };

      const prepareLoopItems = () => {
        if (!this.gallery) return;
        this.gallery.querySelectorAll(LOOP_CLONE_SELECTOR).forEach((clone) => clone.remove());
        const items = galleryItems();
        items.forEach((item, idx) => {
          item.dataset.pdpGalleryRealIndex = String(idx);
          item.removeAttribute('data-pdp-gallery-loop-clone');
        });
        const loopBuffer = loopBufferSize(items.length);
        if (loopBuffer) {
          this.gallery.insertBefore(cloneFragment(items.slice(-loopBuffer)), items[0]);
          this.gallery.appendChild(cloneFragment(items.slice(0, loopBuffer)));
        }
        refreshGalleryCount();
      };
      this.prepareGalleryLoop = prepareLoopItems;
      prepareLoopItems();

      const clampStageIndex = (index) => Math.max(0, Math.min(galleryItemCount - 1, Number(index) || 0));
      const wrapStageIndex = (index) => galleryItemCount
        ? ((Number(index) % galleryItemCount) + galleryItemCount) % galleryItemCount
        : 0;
      const stageIndex = () => clampStageIndex(this.galleryStageIndex);
      const realItemForIndex = (index) => galleryItems().find((item) => itemRealIndex(item) === index) || galleryItems()[index];
      const nearestItemFromScroll = (scrollLeft = this.gallery?.scrollLeft || 0) => {
        const items = allGalleryItems();
        if (!items.length) return null;
        return items.reduce((closest, item) => {
          if (!closest) return item;
          return Math.abs(item.offsetLeft - scrollLeft) < Math.abs(closest.offsetLeft - scrollLeft) ? item : closest;
        }, null);
      };
      const realIndexFromScroll = (scrollLeft = this.gallery?.scrollLeft || 0) => {
        const fallback = stageIndex();
        const item = nearestItemFromScroll(scrollLeft);
        const index = itemRealIndex(item);
        return index >= 0 ? clampStageIndex(index) : fallback;
      };
      const progressIndexFromScroll = () => {
        const items = galleryItems();
        if (!this.gallery || galleryItemCount <= 1 || items.length <= 1) return stageIndex();
        const step = Math.max(1, (items[1]?.offsetLeft || 0) - (items[0]?.offsetLeft || 0) || this.gallery.clientWidth || 1);
        const raw = (this.gallery.scrollLeft - items[0].offsetLeft) / step;
        if (raw < 0 || raw > galleryItemCount - 1) return stageIndex();
        return Math.max(0, Math.min(galleryItemCount - 1, raw));
      };
      const reflectActiveStage = (nextIndex) => {
        const desktop = isDesktopStage();
        this.gallery?.classList.add('is-stage-ready');
        allGalleryItems().forEach((item) => {
          const clone = item.matches(LOOP_CLONE_SELECTOR);
          const active = !clone && itemRealIndex(item) === nextIndex;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-hidden', clone || (desktop && !active) ? 'true' : 'false');
          item.tabIndex = clone ? -1 : (desktop ? (active ? 0 : -1) : 0);
        });
      };

      const setStageIndex = (index = 0, options = {}) => {
        refreshGalleryCount();
        if (!galleryItemCount) return;
        const wrap = options.wrap !== false;
        const nextIndex = wrap ? wrapStageIndex(index) : clampStageIndex(index);
        this.galleryStageIndex = nextIndex;
        reflectActiveStage(nextIndex);
        if (options.scroll !== false) {
          const activeItem = realItemForIndex(nextIndex);
          if (activeItem) {
            const left = activeItem.offsetLeft;
            const behavior = options.behavior || 'smooth';
            this.gallery.scrollTo({ left, behavior });
          }
        }
        this.requestGalleryProgressUpdate?.();
      };

      const moveStage = (direction, options = {}) => {
        refreshGalleryCount();
        if (galleryItemCount <= 1 || !direction) return false;
        const current = stageIndex();
        const next = options.wrap === false ? clampStageIndex(current + direction) : wrapStageIndex(current + direction);
        if (next === current) return false;
        setStageIndex(next, options);
        return true;
      };

      this.refreshGalleryStage = (index = this.galleryStageIndex || 0, behavior = 'auto') => {
        this.prepareGalleryLoop?.();
        refreshGalleryCount();
        setStageIndex(index, { wrap: true, behavior });
      };

      if (prevButton) {
        this.on(prevButton, 'click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          moveStage(-1);
        });
      }
      if (nextButton) {
        this.on(nextButton, 'click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          moveStage(1);
        });
      }

      if (!this.gallery || !this.galleryProgressBar) return;

      const interactiveSelector = 'a, button, input, select, textarea, summary, [role="button"], [contenteditable="true"]';
      const isRealGalleryControl = (target) => {
        if (!(target instanceof Element)) return false;
        const interactive = target.closest(interactiveSelector);
        if (!interactive) return false;
        return !interactive.classList.contains('pdp-clean__gallery-item');
      };
      const syncStageFromScroll = () => {
        if (galleryItemCount <= 1) return;
        const nextIndex = realIndexFromScroll();
        if (nextIndex === this.galleryStageIndex) return;
        this.galleryStageIndex = nextIndex;
        reflectActiveStage(nextIndex);
      };

      let quietJumpFrame = 0;
      const jumpToStageQuietly = (targetIndex) => {
        if (!this.gallery) return;
        window.cancelAnimationFrame(quietJumpFrame);
        const target = realItemForIndex(targetIndex);
        if (!target) return;
        this.gallery.classList.add('is-quick-jump');
        this.gallery.scrollTo({ left: target.offsetLeft, behavior: 'auto' });
        quietJumpFrame = window.requestAnimationFrame(() => {
          quietJumpFrame = window.requestAnimationFrame(() => {
            this.gallery?.classList.remove('is-quick-jump');
            this.requestGalleryProgressUpdate?.();
          });
        });
      };

      const normalizeLoopPosition = () => {
        refreshGalleryCount();
        if (galleryItemCount <= 1 || !this.gallery) return;
        const nearest = nearestItemFromScroll();
        if (!nearest?.matches(LOOP_CLONE_SELECTOR)) return;
        const realIndex = realIndexFromScroll();
        this.galleryStageIndex = realIndex;
        reflectActiveStage(realIndex);
        jumpToStageQuietly(realIndex);
      };
      this.normalizeGalleryLoop = normalizeLoopPosition;

      let settleScrollFrame = 0;
      let snapTargetLeft = null;
      let snapReleaseTimer = 0;
      const clearSnapTarget = () => {
        snapTargetLeft = null;
        window.clearTimeout(snapReleaseTimer);
      };
      const holdSnapTarget = (left) => {
        snapTargetLeft = left;
        window.clearTimeout(snapReleaseTimer);
        snapReleaseTimer = window.setTimeout(clearSnapTarget, 420);
      };
      const snapToNearestImage = () => {
        refreshGalleryCount();
        if (!this.gallery || galleryItemCount <= 1) return;
        if (snapTargetLeft !== null) {
          if (Math.abs(this.gallery.scrollLeft - snapTargetLeft) > SCROLL_SNAP_TOLERANCE) return;
          clearSnapTarget();
        }
        const nearest = nearestItemFromScroll();
        if (!nearest) return;
        const realIndex = realIndexFromScroll(nearest.offsetLeft);
        this.galleryStageIndex = realIndex;
        reflectActiveStage(realIndex);
        if (nearest.matches(LOOP_CLONE_SELECTOR)) {
          jumpToStageQuietly(realIndex);
          return;
        }
        const delta = Math.abs(this.gallery.scrollLeft - nearest.offsetLeft);
        if (delta <= SCROLL_SNAP_TOLERANCE) return;
        holdSnapTarget(nearest.offsetLeft);
        this.gallery.scrollTo({ left: nearest.offsetLeft, behavior: 'smooth' });
      };
      const settleGalleryScroll = () => {
        window.cancelAnimationFrame(settleScrollFrame);
        settleScrollFrame = window.requestAnimationFrame(() => {
          if (isDesktopStage()) {
            syncStageFromScroll();
            normalizeLoopPosition();
            return;
          }
          snapToNearestImage();
        });
      };

      this.on(this.gallery, 'wheel', (event) => {
        clearSnapTarget();
        if (!isDesktopStage() || galleryItemCount <= 1 || !event.cancelable) return;
        if (isRealGalleryControl(event.target)) return;
        const absX = Math.abs(event.deltaX);
        const absY = Math.abs(event.deltaY);
        if (absX < 12 || absX < absY * 1.35) return;
        event.preventDefault();
        this.gallery.scrollBy({ left: event.deltaX, behavior: 'auto' });
        this.requestGalleryProgressUpdate?.();
      }, { passive: false });
      this.on(this.gallery, 'touchstart', clearSnapTarget, { passive: true });

      let pointerDrag = null;
      let lastPointerDown = 0;
      const dragId = (event) => typeof event.pointerId === 'number' ? event.pointerId : 'mouse';
      const beginDrag = (event, id) => {
        pointerDrag = {
          id,
          index: stageIndex(),
          x: event.clientX,
          y: event.clientY,
          moved: false,
          intent: null,
        };
        if (id !== 'mouse') this.gallery.setPointerCapture?.(id);
        this.gallery.classList.add('is-dragging');
      };
      const resetDrag = (event) => {
        if (!pointerDrag || (event && dragId(event) !== pointerDrag.id)) return;
        if (pointerDrag.id !== 'mouse' && this.gallery.hasPointerCapture?.(pointerDrag.id)) this.gallery.releasePointerCapture(pointerDrag.id);
        this.gallery.classList.remove('is-dragging');
        pointerDrag = null;
      };
      const updateDrag = (event) => {
        if (!pointerDrag || dragId(event) !== pointerDrag.id) return;
        const dx = event.clientX - pointerDrag.x;
        const dy = event.clientY - pointerDrag.y;
        if (!pointerDrag.intent && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
          pointerDrag.intent = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'horizontal' : 'vertical';
        }
        if (pointerDrag.intent === 'horizontal') {
          pointerDrag.moved = true;
          event.preventDefault();
        }
      };
      const finishDrag = (event) => {
        if (!pointerDrag || dragId(event) !== pointerDrag.id) return;
        const dx = event.clientX - pointerDrag.x;
        const dy = event.clientY - pointerDrag.y;
        const shouldMove = pointerDrag.intent === 'horizontal' && Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.15;
        const direction = dx < 0 ? 1 : -1;
        const dragged = pointerDrag.moved;
        const targetIndex = pointerDrag.index + direction;
        resetDrag(event);
        if (!shouldMove) return;
        if (dragged) event.preventDefault();
        setStageIndex(targetIndex);
      };

      this.on(this.gallery, 'pointerdown', (event) => {
        clearSnapTarget();
        if (!isDesktopStage() || galleryItemCount <= 1 || event.button !== 0) return;
        if (isRealGalleryControl(event.target)) return;
        lastPointerDown = Date.now();
        beginDrag(event, event.pointerId);
      });

      this.on(this.gallery, 'pointermove', updateDrag);
      this.on(this.gallery, 'pointerup', finishDrag);
      this.on(this.gallery, 'pointercancel', resetDrag);
      this.on(this.gallery, 'lostpointercapture', resetDrag);

      this.on(this.gallery, 'mousedown', (event) => {
        if (!isDesktopStage() || galleryItemCount <= 1 || event.button !== 0) return;
        if (pointerDrag || Date.now() - lastPointerDown < 120) return;
        if (isRealGalleryControl(event.target)) return;
        beginDrag(event, 'mouse');
      });
      this.on(document, 'mousemove', updateDrag);
      this.on(document, 'mouseup', finishDrag);

      this.on(document, 'keydown', (event) => {
        if (!isDesktopStage() || galleryItemCount <= 1) return;
        if (this.lightbox?.classList.contains('is-open')) return;
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        const target = event.target;
        if (target && target !== document.body) {
          if (target.closest('input, textarea, select, [contenteditable="true"], [data-pdp-tabs], [data-pdp-dropdown], .pdp-clean__picker, .pdp-clean__sizeguide')) return;
        }
        event.preventDefault();
        moveStage(event.key === 'ArrowRight' ? 1 : -1);
      });

      const update = () => {
        this.raf = null;
        refreshGalleryCount();
        syncStageFromScroll();
        const count = galleryItemCount;
        if (count <= 1) return;
        const segment = 100 / count;
        this.galleryProgressBar.style.width = `${segment}%`;
        this.galleryProgressBar.style.left = '0';
        this.galleryProgressBar.style.transform = `translateX(${progressIndexFromScroll() * 100}%)`;
      };

      this.requestGalleryProgressUpdate = () => {
        if (this.raf) return;
        this.raf = window.requestAnimationFrame(update);
      };

      let scrollEndTimer = 0;
      this.on(this.gallery, 'scroll', () => {
        this.requestGalleryProgressUpdate();
        window.clearTimeout(scrollEndTimer);
        scrollEndTimer = window.setTimeout(() => {
          settleGalleryScroll();
          this.requestGalleryProgressUpdate();
        }, SCROLL_SETTLE_MS);
      }, { passive: true });
      if ('onscrollend' in window) {
        this.on(this.gallery, 'scrollend', () => {
          refreshGalleryCount();
          settleGalleryScroll();
          this.requestGalleryProgressUpdate();
        }, { passive: true });
      }
      this.on(window, 'resize', () => { this.refreshGalleryStage?.(); this.requestGalleryProgressUpdate(); }, { passive: true });
      this.on(window, 'orientationchange', () => { this.refreshGalleryStage?.(); this.requestGalleryProgressUpdate(); }, { passive: true });
      setStageIndex(0, { behavior: 'auto' });
      this.requestGalleryProgressUpdate();
    }

    bindSizeGuide() {
      const modal = this.section.querySelector('[data-pdp-sizeguide]');
      if (!modal) return;
      const open = () => {
        modal.hidden = false;
        document.documentElement.style.overflow = 'hidden';
        const c = modal.querySelector('.pdp-clean__sizeguide-close');
        if (c) window.requestAnimationFrame(() => c.focus());
      };
      const close = () => {
        modal.hidden = true;
        document.documentElement.style.overflow = '';
      };
      this.section.querySelectorAll('[data-pdp-sizeguide-open]').forEach((btn) => {
        this.on(btn, 'click', (e) => { e.preventDefault(); open(); });
      });
      modal.querySelectorAll('[data-pdp-sizeguide-close]').forEach((el) => {
        this.on(el, 'click', close);
      });
      this.on(document, 'keydown', (e) => { if (e.key === 'Escape' && !modal.hidden) close(); });
    }

    bindModelInfoOverlay() {
      const buttons = Array.from(this.section.querySelectorAll('[data-pdp-model-toggle]'));
      if (!buttons.length) return;

      const pairs = buttons.map((button) => {
        const item = button.closest('.pdp-clean__gallery-item') || button.closest('[data-pdp-gallery-wrap]');
        const id = button.getAttribute('aria-controls');
        const overlay = id
          ? this.section.querySelector(`#${cssEscape(id)}`)
          : item?.querySelector('[data-pdp-model-overlay]');

        return (item && overlay) ? { button, item, overlay } : null;
      }).filter(Boolean);

      if (!pairs.length) return;

      const setOpen = ({ button, item, overlay }, open) => {
        overlay.hidden = !open;
        item.classList.toggle('is-model-open', open);
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
      };

      const closeAll = (except = null) => {
        pairs.forEach((pair) => {
          if (pair !== except) setOpen(pair, false);
        });
      };

      pairs.forEach((pair) => {
        this.on(pair.button, 'click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const shouldOpen = pair.overlay.hidden;
          closeAll(pair);
          setOpen(pair, shouldOpen);
        });

        this.on(pair.button, 'keydown', (event) => {
          event.stopPropagation();
        });
      });

      this.on(document, 'keydown', (event) => {
        if (event.key === 'Escape') closeAll();
      });
    }

    bindReviewsJump() {
      const links = this.section.querySelectorAll('[data-pdp-jump-reviews]');
      const target = findReviewsSection(this.section) || this.section.querySelector('[data-pdp-accordions]');
      if (!links.length || !target) return;
      const jump = (event) => {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation?.();
        }
        this.activateReviewsTab?.();
        this.pokeKlaviyoReviews();
        const scrollToReviews = (behavior = 'smooth') => {
          const headerVar = parseInt(window.getComputedStyle(document.documentElement).getPropertyValue('--HEADER-HEIGHT'), 10);
          const header = Number.isFinite(headerVar) ? headerVar : (document.querySelector('header')?.offsetHeight || 0);
          const top = target.getBoundingClientRect().top + window.scrollY - header - 12;
          window.scrollTo({ top: Math.max(0, top), behavior });
          if (target.id) window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${target.id}`);
        };
        window.requestAnimationFrame(() => {
          scrollToReviews('smooth');
          window.setTimeout(() => {
            const headerVar = parseInt(window.getComputedStyle(document.documentElement).getPropertyValue('--HEADER-HEIGHT'), 10);
            const header = Number.isFinite(headerVar) ? headerVar : (document.querySelector('header')?.offsetHeight || 0);
            const delta = Math.abs(target.getBoundingClientRect().top - header - 12);
            if (delta > 36) scrollToReviews('auto');
            target.setAttribute('tabindex', '-1');
            target.focus?.({ preventScroll: true });
          }, 620);
        });
      };
      links.forEach((link) => {
        if (link.tagName !== 'A') {
          if (!link.hasAttribute('tabindex')) link.tabIndex = 0;
          if (!link.hasAttribute('role')) link.setAttribute('role', 'button');
        }
        this.on(link, 'click', jump, { capture: true });
        this.on(link, 'keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          jump(event);
        });
      });
    }

    bindKundeneSier() {
      // Keep the buy column deterministic. Klaviyo can hydrate the rating
      // summary and the full reviews section, but it must not inject a delayed
      // proof card into the decision stack. If a product needs a featured quote,
      // use the product metafields rendered in Liquid instead.
      const slot = this.section.querySelector('[data-pdp-kundene-slot]');
      if (slot) {
        slot.hidden = true;
        slot.innerHTML = '';
        slot.removeAttribute('role');
        slot.removeAttribute('tabindex');
        slot.removeAttribute('aria-label');
        slot.style.removeProperty('cursor');
      }
    }

    bindStickyPicker() {
      // LSF-stil: inline size-picker i sticky-bar. Native <select> for kompakthet,
      // mirrorer hoved-pickerens valg + opdaterer ved variant-change.
      const slot = this.section.querySelector('[data-pdp-sticky-picker]');
      if (!slot) return;
      // Finn første ikke-color picker (typisk størrelse). Bundle-set bruker
      // delt picker — vi mirrorer den om finnes, ellers første size-group.
      const sizeGroup = this.optionGroups.find((g) => {
        const legend = (g.querySelector('legend')?.textContent || '').toLowerCase();
        return !/farge|color|colour/.test(legend);
      });
      if (!sizeGroup) return;
      const inputs = Array.from(sizeGroup.querySelectorAll('[data-pdp-option-input]'));
      if (inputs.length < 2) return;

      const select = document.createElement('select');
      select.className = 'pdp-clean__sticky-select';
      select.setAttribute('aria-label', 'Velg størrelse');
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Velg størrelse';
      placeholder.disabled = true;
      placeholder.selected = true;
      select.appendChild(placeholder);
      inputs.forEach((input) => {
        const opt = document.createElement('option');
        opt.value = input.value;
        opt.textContent = input.value;
        opt.disabled = input.disabled;
        select.appendChild(opt);
      });
      slot.appendChild(select);
      slot.hidden = false;

      // Change i sticky-select →
      // 1) Hvis bundle-shared finnes: click matching pill (som synker til alle 3)
      // 2) Ellers: check radio i hoved-pickeren
      this.on(select, 'change', () => {
        const value = select.value;
        if (!value) return;
        const sharedPill = this.section.querySelector(`[data-pdp-bundle-shared]:not([hidden]) .pdp-clean__bundle-shared-pill[data-value="${cssEscape(value)}"]`);
        if (sharedPill) {
          sharedPill.click();
          return;
        }
        // For bundle uten shared (mix-mode aktiv): sync til ALLE size-grupper
        const allSizeGroups = this.optionGroups.filter((g) => {
          const legend = (g.querySelector('legend')?.textContent || '').toLowerCase();
          return !/farge|color|colour/.test(legend);
        });
        allSizeGroups.forEach((group) => {
          const radio = group.querySelector(`[data-pdp-option-input][value="${cssEscape(value)}"]`);
          if (radio && !radio.disabled) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
      // Når hoved-radio endres → speil i sticky-select
      inputs.forEach((input) => {
        this.on(input, 'change', () => {
          if (input.checked) select.value = input.value;
        });
      });
    }

    bindSticky() {
      if (!this.sticky || !this.stickyButton || !this.atc) return;
      this.bindStickyPicker();
      this.on(this.stickyButton, 'click', () => {
        if (!this.form || this.stickyButton.disabled) return;
        // I "pick"-mode: scroll til STØRRELSE-gruppen + highlight den. IKKE add-to-cart.
        // Viktig: optionGroups[0] er Farge (pickere rendres farge-først), så vi må
        // finne første ikke-farge-gruppe (størrelse), ikke bare ta [0].
        if (this.stickyButton.dataset.mode === 'pick') {
          // Stabil target: Liquid markerer størrelse-fieldset med data-pdp-size-group.
          // Fallback til legend-tekst kun hvis attributtet mangler (eldre markup).
          const sharedPicker = this.section.querySelector('[data-pdp-bundle-shared]:not([hidden])');
          const sizeGroup = this.section.querySelector('[data-pdp-size-group]')
            || this.optionGroups.find((g) => {
              const legend = (g.querySelector('legend')?.textContent || '').toLowerCase();
              return !/farge|color|colour/.test(legend);
            })
            || this.optionGroups[0];
          const target = sharedPicker || sizeGroup;
          if (target) {
            // Åpne ev. dropdown (kun hvis dropdown-modus er på)
            target.querySelectorAll('[data-pdp-dropdown]').forEach((d) => { d.open = true; });
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Fokuser dropdown-trigger (dropdown-modus) eller første ledige størrelse (knapp-modus)
            const focusEl = target.querySelector('.pdp-clean__bundle-shared-pill:not(:disabled), .pdp-clean__dropdown-trigger, [data-pdp-option-input]:not(:disabled)');
            if (focusEl && typeof focusEl.focus === 'function') {
              try { focusEl.focus({ preventScroll: true }); } catch (e) { focusEl.focus(); }
            }
            // Visuell puls så brukeren ser HVOR de skal velge
            target.classList.add('pdp-clean__picker--flash');
            window.setTimeout(() => target.classList.remove('pdp-clean__picker--flash'), 1300);
          }
          return;
        }
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.atc);
        else this.atc.click();
      });

      const request = () => {
        if (this.stickyRaf) return;
        this.stickyRaf = window.requestAnimationFrame(() => {
          this.stickyRaf = null;
          this.updateStickyVisibility();
        });
      };
      const requestWithMetrics = () => {
        this.refreshStickyMetrics();
        request();
      };
      this.on(window, 'scroll', request, { passive: true });
      this.on(window, 'resize', requestWithMetrics, { passive: true });
      this.on(window, 'orientationchange', requestWithMetrics, { passive: true });
      this.updateStickyVisibility();
    }

    updateStickyVisibility() {
      if (!this.sticky || !this.atc) return;
      // Show the sticky CTA whenever the main CTA is outside the active buying
      // area. On mobile this covers both states: before the user reaches the
      // size picker, and after the main CTA has scrolled past.
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      const atcRect = this.atc.getBoundingClientRect();
      let galleryVisible = false;
      if (isMobile && this.galleryWrap) {
        const galleryRect = this.galleryWrap.getBoundingClientRect();
        galleryVisible = galleryRect.bottom > 0 && galleryRect.top < window.innerHeight;
      }
      const stickyHeight = isMobile
        ? this.mobileStickyHeight
        : (this.sticky.hidden ? 64 : this.sticky.getBoundingClientRect().height);
      const activeBottom = window.innerHeight - stickyHeight - 12;
      const show = isMobile
        ? atcRect.top > activeBottom || atcRect.bottom < 0
        : atcRect.bottom < 0;
      this.sticky.hidden = !show;
      this.section.classList.toggle('pdp-clean--sticky-visible', show);
      this.section.classList.toggle('pdp-clean--gallery-visible', galleryVisible);
      if (isMobile && show) this.refreshStickyMetrics();
    }
  }

  const initSection = (section) => {
    if (!section || instances.has(section)) return;
    primeReviewFallback(section);
    const instance = new LaleyPDP(section);
    instances.set(section, instance);
    instance.init();
  };

  document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.matches?.(SECTION_SELECTOR) ? event.target : event.target.querySelector?.(SECTION_SELECTOR);
    initSection(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.(SECTION_SELECTOR) ? event.target : event.target.querySelector?.(SECTION_SELECTOR);
    const instance = section ? instances.get(section) : null;
    if (instance) instance.destroy();
  });
})();
